import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Eye,
  X,
  Search,
  Package,
  User,
  Receipt,
  Printer,
  Pencil,
  Trash2,
  AlertTriangle,
  Save,
  ChevronDown,
  Loader2,
  Plus,
  CheckCircle2,
  Clock,
  Tag,
  Minus,
  Truck,
  RotateCcw,
  Star,
  Calendar,
  IndianRupee,
  AlertCircle,
  LockKeyhole,
} from "lucide-react";
import { toast } from "sonner";
import { DateRangePicker } from "../components/DateRangePicker";
import { AutoSizer, List as VirtualizedList } from "react-virtualized";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Formatters ────────────────────────────────────────────────
const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";
const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
const fmtFull = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
const rupee = (n) => `₹${(n || 0).toFixed(2)}`;
const toDateInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Today's date string in YYYY-MM-DD
const todayStr = () => new Date().toISOString().split("T")[0];
const isToday = (dateStr) => dateStr && dateStr === todayStr();
const isPast = (dateStr) => dateStr && dateStr < todayStr();
const isFuture = (dateStr) => dateStr && dateStr > todayStr();
const yesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};
const startOfWeekStr = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  return d.toISOString().split("T")[0];
};
const startOfLastWeekStr = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1) - 7);
  return d.toISOString().split("T")[0];
};
const endOfLastWeekStr = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? 0 : 7) - 7);
  return d.toISOString().split("T")[0];
};
const startOfMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const startOfLastMonthStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const endOfLastMonthStr = () => {
  const d = new Date();
  d.setDate(0);
  return d.toISOString().split("T")[0];
};
const PAYMENT_COLORS = {
  cash: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  card: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  upi: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  split: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  credit: "bg-rose-500/15 text-rose-400 border-rose-500/20",
};
const pmColor = (m) => {
  console.log(m);
  return (
    PAYMENT_COLORS[m?.toLowerCase()] ||
    "bg-stone-500/15 text-stone-400 border-stone-500/20"
  );
};

const COMPANY_SETTINGS = {
  name: "M. R. B. SAnitary & Ceramic",
  address:
    "GF, Shed NO. 4,5,6 Survey No. 1525/83, TP.54, FP No.29, Jay Ambe Estate, Ognaj Circle, Opp. Purohit Hotel, Ahmedabad",
  phone: "9737012222",
  gstin: "24AUAPJ3156A1ZC",
  state: "Gujarat, Code : 24",
  email: "mrbceramic406@gmail.com",
  pan: "AUAPJ3156A",
  bankName: "ICICI BANK",
  bankAccount: "182805500120",
  bankIFSC: "ICIC0001828",
  bankBranch: "Chandlodiya, Ahmedabad",
};

// ─── Number to words ───────────────────────────────────────────
const numberToWords = (amount) => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const inWords = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n] + " ";
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] +
        (ones[n % 10] ? " " + ones[n % 10] : "") +
        " "
      );
    if (n < 1000)
      return ones[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
    if (n < 100000)
      return inWords(Math.floor(n / 1000)) + "Thousand " + inWords(n % 1000);
    if (n < 10000000)
      return inWords(Math.floor(n / 100000)) + "Lakh " + inWords(n % 100000);
    return inWords(Math.floor(n / 10000000)) + "Crore " + inWords(n % 10000000);
  };
  const rounded = Math.round(amount * 100) / 100;
  const intPart = Math.floor(rounded);
  const decPart = Math.round((rounded - intPart) * 100);
  let words =
    "Indian Rupees " + (intPart === 0 ? "Zero" : inWords(intPart).trim());
  if (decPart > 0) words += " and " + inWords(decPart).trim() + " Paise";
  return words + " Only";
};

// ─── Helper: compute received & balance from order ─────────────
// Handles the case where amount_received was not saved correctly
// but credit_cash/card/upi breakdown was saved properly.
const getReceivedAndBalance = (order) => {
  // amount_received is updated as a running total on every collection save.
  // Only fall back to the credit breakdown if amount_received was never set (legacy data).
  const fromBreakdown =
    (order.credit_cash || 0) +
    (order.credit_card || 0) +
    (order.credit_upi || 0);

  const collectionTotal = (order.payment_collections || []).reduce(
    (sum, collection) => sum + (parseFloat(collection.amount) || 0),
    0,
  );
  const received = Math.max(
    parseFloat(order.amount_received) || 0,
    parseFloat(order.amount_paid) || 0,
    fromBreakdown + collectionTotal,
    collectionTotal,
  );
  const balance = Math.max(0, (parseFloat(order.total) || 0) - received);

  return { received, balance };
};

const getPaymentLabel = (order) => {
  const paidMethods = [
    order.credit_cash,
    order.credit_card,
    order.credit_upi,
  ].filter((amount) => (parseFloat(amount) || 0) > 0);
  if (paidMethods.length > 1) return "Split";
  if ((parseFloat(order.credit_card) || 0) > 0) return "Card";
  if ((parseFloat(order.credit_cash) || 0) > 0) return "Cash";
  if ((parseFloat(order.credit_upi) || 0) > 0) return "UPI";
  const mode = String(order.payment_mode || "").trim();
  if (mode && !["pending", "credit"].includes(mode.toLowerCase())) return mode;
  if (
    getReceivedAndBalance(order).received >= (order.total || 0) &&
    (order.total || 0) > 0
  )
    return "Paid";
  return mode.toLowerCase() === "credit" ? "Credit" : "Pending";
};

const normalizePaymentMode = (mode) => {
  const normalized = String(mode || "")
    .trim()
    .toLowerCase();
  if (normalized === "bank transfer" || normalized === "bank_transfer")
    return "cash";
  if (["cash", "card", "upi", "split", "credit"].includes(normalized))
    return normalized;
  return "cash";
};

// ─── Invoice builders ──────────────────────────────────────────
const buildNonGSTInvoice = (order, userName) => {
  if (!order) return "";

  // FIX: derive correct received/balance from credit breakdown
  const { received: computedReceived, balance: computedBalance } =
    getReceivedAndBalance(order);

  const rows = (order.items || [])
    .map(
      (item) => `
        <tr>
          <td style="padding:5px 3px;border-bottom:1px dashed #ddd;">${item.product_name}</td>
          <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:center;">${item.quantity}</td>
          <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:right;">₹${(item.price || 0).toFixed(2)}</td>
          <td style="padding:5px 3px;border-bottom:1px dashed #ddd;text-align:right;font-weight:700;">₹${(item.total || 0).toFixed(2)}</td>
        </tr>`,
    )
    .join("");
  const d = new Date(order.created_at);
  const extraDisc = order.extra_discount || 0;

  const actualTotalAfterDiscount = Math.max(
    0,
    (order.subtotal || 0) - (order.discount || 0) - extraDisc,
  );
  const isPendingPartial = order.status === "partial";
  const isPending = order.status === "pending_delivery";

  // Build payment breakdown lines for partial orders
  const paymentBreakdownHTML = isPendingPartial
    ? `
      <div style="border-top:2px dashed #000;padding-top:8px;margin-top:8px;">
        <div style="font-weight:900;font-size:11px;margin-bottom:5px;text-align:center;letter-spacing:1px;">— PAYMENT DETAILS —</div>
        ${(order.credit_cash || 0) > 0 ? `<div style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0;"><span>💵 Cash Paid</span><span>₹${(order.credit_cash || 0).toFixed(2)}</span></div>` : ""}
        ${(order.credit_card || 0) > 0 ? `<div style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0;"><span>💳 Card Paid</span><span>₹${(order.credit_card || 0).toFixed(2)}</span></div>` : ""}
        ${(order.credit_upi || 0) > 0 ? `<div style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0;"><span>📲 UPI Paid</span><span>₹${(order.credit_upi || 0).toFixed(2)}</span></div>` : ""}
        <div style="display:flex;justify-content:space-between;font-weight:700;font-size:12px;color:#15803d;border-top:1px solid #ddd;padding-top:4px;margin-top:4px;"><span>✅ Total Paid</span><span>₹${computedReceived.toFixed(2)}</span></div>
        <div style="display:flex;justify-content:space-between;font-weight:700;font-size:12px;color:#c00;"><span>⚠ Balance Due on Delivery</span><span>₹${computedBalance.toFixed(2)}</span></div>
      </div>`
    : "";

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
      <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Courier New',monospace;font-size:12px;width:300px;margin:0 auto;padding:16px;}
      .center{text-align:center;}.bold{font-weight:700;}table{width:100%;border-collapse:collapse;}
      th{font-size:10px;font-weight:700;border-bottom:2px solid #000;padding:3px;}th:not(:first-child){text-align:right;}
      @media print{body{width:80mm;}@page{margin:0;size:80mm auto;}}</style></head><body>
      <div class="center" style="padding-bottom:10px;border-bottom:2px dashed #000;margin-bottom:10px;">
        <div style="font-size:18px;font-weight:900;letter-spacing:2px;">MRB HOME DECOR</div>
        <div style="font-size:10px;color:#555;margin-top:2px;">Home Decor Mall</div>
      </div>
      <div style="margin-bottom:10px;line-height:1.9;font-size:11px;">
        <div style="display:flex;justify-content:space-between;"><b>Receipt#</b><span>${order.invoice_number}</span></div>
        <div style="display:flex;justify-content:space-between;"><b>Date</b><span>${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>
        <div style="display:flex;justify-content:space-between;"><b>Time</b><span>${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></div>
        <div style="display:flex;justify-content:space-between;"><b>Cashier</b><span>${userName || "Staff"}</span></div>
        ${order.customer_name ? `<div style="display:flex;justify-content:space-between;"><b>Customer</b><span>${order.customer_name}</span></div>` : ""}
        ${order.customer_phone ? `<div style="display:flex;justify-content:space-between;"><b>Phone</b><span>${order.customer_phone}</span></div>` : ""}
        <div style="display:flex;justify-content:space-between;"><b>Payment</b><span style="text-transform:uppercase;">${
          order.credit_card !== 0
            ? "Card"
            : order.credit_cash !== 0
              ? "Cash"
              : order.credit_upi !== 0
                ? "UPI"
                : "Pending"
        }</span></div>
        ${order.scheduled_delivery_date ? `<div style="display:flex;justify-content:space-between;"><b>Delivery Date</b><span>${fmtDate(order.scheduled_delivery_date)}</span></div>` : ""}
      </div>
      <div style="border-top:2px dashed #000;border-bottom:2px dashed #000;padding:8px 0;margin-bottom:8px;">
      <table><thead><tr><th style="text-align:left;">ITEM</th><th>QTY</th><th>RATE</th><th>AMT</th></tr></thead>
      <tbody>${rows}</tbody></table></div>
      <div style="line-height:2;font-size:12px;">
        <div style="display:flex;justify-content:space-between;"><span>Subtotal</span><span>₹${(order.subtotal || 0).toFixed(2)}</span></div>
        ${order.discount > 0 ? `<div style="display:flex;justify-content:space-between;"><span>Discount</span><span>-₹${order.discount.toFixed(2)}</span></div>` : ""}
        ${
          (order.transport_charges || 0) > 0
            ? `<div style="display:flex;justify-content:space-between;">
              <span>Transport Charges</span>
              <span>+₹${(order.transport_charges || 0).toFixed(2)}</span>
            </div>`
            : ""
        }
        ${extraDisc > 0 ? `<div style="display:flex;justify-content:space-between;"><span>Extra Discount</span><span>-₹${extraDisc.toFixed(2)}</span></div>` : ""}
        ${order.round_off && order.round_off !== 0 ? `<div style="display:flex;justify-content:space-between;font-size:10px;color:#666;"><span>Round Off</span><span>${order.round_off > 0 ? "+" : ""}₹${order.round_off.toFixed(2)}</span></div>` : ""}
        <div style="display:flex;justify-content:space-between;font-size:15px;font-weight:900;border-top:2px solid #000;padding-top:5px;margin-top:5px;">
          <span>TOTAL</span><span>₹${(order.total || 0).toFixed(2)}</span></div>
        ${paymentBreakdownHTML}
      </div>
      ${isPending ? `<div style="margin-top:10px;padding:6px;border:2px dashed #f59e0b;border-radius:6px;text-align:center;font-weight:900;font-size:11px;color:#d97706;">📦 PAID · PENDING PICKUP</div>` : ""}
      ${isPendingPartial ? `<div style="margin-top:10px;padding:6px;border:2px dashed #e11d48;border-radius:6px;text-align:center;font-weight:900;font-size:11px;color:#e11d48;">📦 PARTIAL PAYMENT · BALANCE DUE ON DELIVERY</div>` : ""}
      ${order.narration ? `<div style="margin-top:8px;font-size:10px;color:#666;font-style:italic;">${order.narration}</div>` : ""}
      <div style="text-align:center;margin-top:14px;font-size:10px;color:#666;border-top:2px dashed #000;padding-top:10px;">
        <strong style="font-size:12px;display:block;margin-bottom:2px;">Thank you!</strong>
        Please visit again · Items sold are non-refundable without receipt
      </div></body></html>`;
};

const buildGSTInvoice = (order, userName) => {
  if (!order) return "";

  // FIX: derive correct received/balance from credit breakdown
  const { received: computedReceived, balance: computedBalance } =
    getReceivedAndBalance(order);

  const isIGST = order.gst_type === "igst";
  const d = new Date(order.created_at);
  const dateStr = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const orderLevelDiscount =
    (order.discount || 0) + (order.extra_discount || 0);
  const discFactor =
    (order.subtotal || 0) > 0
      ? Math.max(
          0,
          ((order.subtotal || 0) - orderLevelDiscount) / order.subtotal,
        )
      : 1;
  const itemsData = (order.items || []).map((item) => {
    const rate = item.gst_rate != null ? item.gst_rate : 0;
    const grossAmt = item.total || 0;
    // Item totals are GST-inclusive. Apply order-level discounts first, then
    // split the final amount into its taxable value and GST component.
    const netInclusiveAmt = grossAmt * discFactor;
    const itemTaxable =
      rate > 0 ? netInclusiveAmt / (1 + rate / 100) : netInclusiveAmt;
    const gstAmt = rate > 0 ? netInclusiveAmt - itemTaxable : 0;
    const cgst = isIGST ? 0 : gstAmt / 2;
    const sgst = isIGST ? 0 : gstAmt / 2;
    const igst = isIGST ? gstAmt : 0;
    return {
      ...item,
      rate,
      grossAmt,
      originalUnitPrice:
        item.discount_type === "percentage" &&
        Number(item.discount || 0) > 0 &&
        Number(item.discount || 0) < 100
          ? (item.price || 0) / (1 - Number(item.discount || 0) / 100)
          : item.discount_type === "amount"
            ? (item.price || 0) + Number(item.discount || 0)
            : item.price || 0,
      itemTaxable,
      gstAmt,
      cgst,
      sgst,
      igst,
    };
  });
  const itemRowsHTML = itemsData
    .map((item, idx) => {
      const discPct = Number(item.discount || 0);
      const itemSaved =
        item.discount_type === "amount"
          ? Math.max(0, discPct * Number(item.quantity || 1))
          : discPct > 0 && discPct < 100
            ? Math.max(0, item.grossAmt / (1 - discPct / 100) - item.grossAmt)
            : 0;
      const originalUnitPrice = item.originalUnitPrice;
      const gstLines =
        item.rate > 0
          ? isIGST
            ? `<tr><td colspan="7" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">IGST @ ${item.rate}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.igst.toFixed(2)}</td><td></td></tr>`
            : `<tr><td colspan="7" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">CGST @ ${item.rate / 2}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.cgst.toFixed(2)}</td><td></td></tr><tr><td colspan="7" style="text-align:right;border-right:1px solid #aaa;padding:2px 5px;font-size:10px;color:#333;">SGST @ ${item.rate / 2}%</td><td style="text-align:right;padding:2px 5px;font-size:10px;">${item.sgst.toFixed(2)}</td><td></td></tr>`
          : "";
      return `<tr style="border-bottom:1px solid #ccc;"><td style="text-align:center;vertical-align:top;">${idx + 1}</td><td style="vertical-align:top;">${item.product_name}${item.hsn ? `<br/><span style="font-size:9px;color:#555;">HSN: ${item.hsn}</span>` : ""}</td><td style="text-align:center;vertical-align:top;">${item.hsn || ""}</td><td style="text-align:center;vertical-align:top;">${item.quantity} Pcs</td><td style="text-align:right;vertical-align:top;">${originalUnitPrice.toFixed(2)}</td><td style="text-align:center;vertical-align:top;">Pcs</td><td style="text-align:right;vertical-align:top;">${discPct ? `${discPct.toFixed(2)}%` : ""}</td><td style="text-align:right;vertical-align:top;">₹${itemSaved.toFixed(2)}</td><td style="text-align:right;vertical-align:top;">${item.grossAmt.toFixed(2)}</td></tr>${gstLines}`;
    })
    .join("");
  const itemDiscountTotal = itemsData.reduce((sum, item) => {
    const discount = Number(item.discount || 0);
    const saved =
      item.discount_type === "amount"
        ? discount * Number(item.quantity || 1)
        : discount > 0 && discount < 100
          ? item.grossAmt / (1 - discount / 100) - item.grossAmt
          : 0;
    return sum + Math.max(0, saved);
  }, 0);

  const totalQuantity = itemsData.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );
  const extraDisc = order.extra_discount || 0;
  const actualTotalAfterDiscount = Math.max(
    0,
    (order.subtotal || 0) - (order.discount || 0) - extraDisc,
  );
  const totalBeforeItemDiscount = itemsData.reduce(
    (sum, item) => sum + item.originalUnitPrice * Number(item.quantity || 0),
    0,
  );

  const hsnMap = {};
  itemsData.forEach((item) => {
    const hsnCode = item.hsn || "N/A";
    const key = `${hsnCode}__${item.rate}`; // composite key: separate rows per rate, even under the same/blank HSN
    if (!hsnMap[key])
      hsnMap[key] = {
        hsn: hsnCode,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        rate: item.rate,
      };
    hsnMap[key].taxable += item.itemTaxable;
    hsnMap[key].cgst += item.cgst;
    hsnMap[key].sgst += item.sgst;
    hsnMap[key].igst += item.igst;
  });
  const hsnTotalTaxable = Object.values(hsnMap).reduce(
    (s, v) => s + v.taxable,
    0,
  );
  const hsnTotalCGST = Object.values(hsnMap).reduce((s, v) => s + v.cgst, 0);
  const hsnTotalSGST = Object.values(hsnMap).reduce((s, v) => s + v.sgst, 0);
  const hsnTotalIGST = Object.values(hsnMap).reduce((s, v) => s + v.igst, 0);
  const hsnTotalTax = hsnTotalCGST + hsnTotalSGST + hsnTotalIGST;

  const isPendingPartial = order.status === "partial";
  const isPending = order.status === "pending_delivery";

  // Payment breakdown HTML for GST invoice (partial orders)
  const paymentBreakdownSectionHTML = isPendingPartial
    ? `
    <table class="outer-border" style="margin-top:-1px;border-top:none;">
      <tr>
        <td style="padding:8px 10px;">
          <div style="font-weight:bold;font-size:11px;margin-bottom:6px;text-decoration:underline;">Payment Details (Partial Payment)</div>
          <table style="width:100%;border-collapse:collapse;font-size:10px;">
            ${(order.credit_cash || 0) > 0 ? `<tr><td style="padding:2px 0;">💵 Cash Paid</td><td style="text-align:right;font-weight:bold;">₹${(order.credit_cash || 0).toFixed(2)}</td></tr>` : ""}
            ${(order.credit_card || 0) > 0 ? `<tr><td style="padding:2px 0;">💳 Card Paid</td><td style="text-align:right;font-weight:bold;">₹${(order.credit_card || 0).toFixed(2)}</td></tr>` : ""}
            ${(order.credit_upi || 0) > 0 ? `<tr><td style="padding:2px 0;">📲 UPI Paid</td><td style="text-align:right;font-weight:bold;">₹${(order.credit_upi || 0).toFixed(2)}</td></tr>` : ""}
            <tr style="border-top:1px solid #000;">
              <td style="padding:3px 0;font-weight:bold;color:#15803d;">✅ Total Paid</td>
              <td style="text-align:right;font-weight:bold;color:#15803d;">₹${computedReceived.toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding:2px 0;font-weight:bold;color:#dc2626;">⚠ Balance Due on Delivery</td>
              <td style="text-align:right;font-weight:bold;color:#dc2626;">₹${computedBalance.toFixed(2)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`
    : "";

  const buildGSTInvoiceTotalsSection = (order, roundOff, amtWords) => {
    const transport = order.transport_charges || 0;
    return `
  <table class="outer-border" style="margin-top: -1px; border-top: none">
    <tr>
      <td style="width: 62%; border-right: 1px solid #000" rowspan="${transport > 0 ? 4 : 3}">
        <div style="font-weight: bold; margin-bottom: 4px">
          Amount Chargeable (in words)
        </div>
        <div style="font-style: italic">${amtWords}</div>
        ${
          isPendingPartial
            ? `
        <div style="margin-top: 6px; font-size: 10px; border-top:1px dashed #ccc; padding-top:5px;">
          ${(order.credit_cash || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>💵 Cash Paid:</span><span style="font-weight:bold;">₹${(order.credit_cash || 0).toFixed(2)}</span></div>` : ""}
          ${(order.credit_card || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>💳 Card Paid:</span><span style="font-weight:bold;">₹${(order.credit_card || 0).toFixed(2)}</span></div>` : ""}
          ${(order.credit_upi || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>📲 UPI Paid:</span><span style="font-weight:bold;">₹${(order.credit_upi || 0).toFixed(2)}</span></div>` : ""}
          <div style="display:flex;justify-content:space-between;font-weight:bold;color:#15803d;border-top:1px solid #ddd;margin-top:2px;padding-top:2px;"><span>✅ Total Paid:</span><span>₹${computedReceived.toFixed(2)}</span></div>
          <div style="display:flex;justify-content:space-between;font-weight:bold;color:#dc2626;"><span>⚠ Balance Due:</span><span>₹${computedBalance.toFixed(2)}</span></div>
        </div>
        `
            : ""
        }
        <div style="font-size: 9px; margin-top: 4px; color: #555">
          E. &amp; O.E
        </div>
      </td>
      ${
        Math.abs(roundOff) >= 0.01
          ? `<td style="text-align:right;border-right:1px solid #000;padding:3px 6px;width:26%;">
              Less : Round Off
            </td>
            <td style="text-align:right;padding:3px 6px;width:12%;">
              ${roundOff < 0 ? "(-)" : "(+)"} ₹${Math.abs(roundOff).toFixed(2)}
            </td>`
          : `<td style="border:none" colspan="2"></td>`
      }
    </tr>
    ${
      transport > 0
        ? `
    <tr>
      <td style="text-align:right;border-right:1px solid #000;border-top:1px solid #ddd;padding:3px 6px;color:#2563eb;">
        🚚 Transport Charges
      </td>
      <td style="text-align:right;border-top:1px solid #ddd;padding:3px 6px;color:#2563eb;">
        + ₹${transport.toFixed(2)}
      </td>
    </tr>`
        : ""
    }
    <tr>
      <td style="text-align:right;font-weight:bold;font-size:13px;border-right:1px solid #000;border-top:1px solid #000;padding:4px 6px;">
        Total
      </td>
      <td style="text-align:right;font-weight:bold;font-size:13px;border-top:1px solid #000;padding:4px 6px;">
        ₹ ${(order.total || 0).toFixed(2)}
      </td>
    </tr>
  </table>`;
  };

  const hsnTableHTML = isIGST
    ? `<table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:-1px;"><thead><tr style="background:#f4f4f4;"><th style="border:1px solid #000;padding:3px 5px;text-align:left;">HSN/SAC</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Taxable Value</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">IGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">IGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Total Tax Amount</th></tr></thead><tbody>${Object.entries(
        hsnMap,
      )
        .map(
          ([hsn, data]) =>
            `<tr><td style="border:1px solid #000;padding:3px 5px;">${data.hsn}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.taxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.igst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.igst.toFixed(2)}</td></tr>`,
        )
        .join(
          "",
        )}<tr style="font-weight:bold;"><td style="border:1px solid #000;padding:3px 5px;">Total</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTaxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalIGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTax.toFixed(2)}</td></tr></tbody></table>`
    : `<table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:-1px;"><thead><tr style="background:#f4f4f4;"><th style="border:1px solid #000;padding:3px 5px;text-align:left;">HSN/SAC</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Taxable Value</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">CGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">CGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:center;">SGST/UTGST Rate</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">SGST/UTGST Amount</th><th style="border:1px solid #000;padding:3px 5px;text-align:right;">Total Tax Amount</th></tr></thead><tbody>${Object.entries(
        hsnMap,
      )
        .map(
          ([hsn, data]) =>
            `<tr><td style="border:1px solid #000;padding:3px 5px;">${hsn}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.taxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate / 2}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.cgst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:center;">${data.rate / 2}%</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${data.sgst.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${(data.cgst + data.sgst).toFixed(2)}</td></tr>`,
        )
        .join(
          "",
        )}<tr style="font-weight:bold;"><td style="border:1px solid #000;padding:3px 5px;">Total</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTaxable.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalCGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;"></td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalSGST.toFixed(2)}</td><td style="border:1px solid #000;padding:3px 5px;text-align:right;">${hsnTotalTax.toFixed(2)}</td></tr></tbody></table>`;

  const amtWords = numberToWords(order.total || 0);
  const taxWords = numberToWords(hsnTotalTax);
  const roundOff = order.round_off || 0;

  const custName = order.customer_name || "Cash Customer";
  const custAddr = order.customer_address || "";
  const custGSTIN = order.customer_gstin || "";
  const custPhone = order.customer_phone || "";
  const buyerBlock = (label) =>
    `<div style="font-weight:bold;font-size:10px;text-decoration:underline;margin-bottom:4px;">${label}</div><div style="font-weight:bold;">${custName}</div>${custAddr ? `<div style="white-space:pre-line;font-size:10px;line-height:1.5;">${custAddr}</div>` : ""}${custPhone ? `<div style="font-size:10px;">Phone: ${custPhone}</div>` : ""}${custGSTIN ? `<div style="font-size:10px;">GSTIN/UIN: <b>${custGSTIN}</b></div>` : ""}<div style="font-size:10px;">State Name: ${COMPANY_SETTINGS.state}</div>`;
  const transport = order.transport_charges || 0;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Tax Invoice - ${order.invoice_number}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:11px;width:210mm;margin:0 auto;padding:8mm;color:#000;}table{width:100%;border-collapse:collapse;}td,th{padding:3px 5px;vertical-align:top;}.outer-border{border:1px solid #000;}@media print{@page{size:A4 portrait;margin:5mm;}body{width:auto;padding:0;}}</style></head>
    <body>
    <b style="padding-left:5px;font-size:0.8rem;font-weight:600;">For Order Inquiry: ${order.referred_by_staff_name || "—"}</b>
    <table class="outer-border" style="border-bottom:none;"><tr><td colspan="2" style="border-bottom:1px solid #000;border-right:1px solid #000;padding:10px;text-align:center;"><div style="font-size:18px;font-weight:900;letter-spacing:2px;">${COMPANY_SETTINGS.name}</div><div style="font-size:10px;margin-top:2px;">${COMPANY_SETTINGS.address}</div><div style="font-size:10px;">Tel: ${COMPANY_SETTINGS.phone}</div><div style="font-size:10px;">GSTIN/UIN: <b>${COMPANY_SETTINGS.gstin}</b> &nbsp;|&nbsp; State Name: ${COMPANY_SETTINGS.state}</div><div style="font-size:10px;">E-Mail: ${COMPANY_SETTINGS.email}</div>${order.referred_by_staff_name ? `<div style="font-size:10px;margin-top:4px;font-weight:bold;">Staff Referral: ${order.referred_by_staff_name}</div>` : ""}<div style="font-size:13px;font-weight:bold;text-decoration:underline;margin-top:5px;">Tax Invoice</div></td><td style="border-bottom:1px solid #000;padding:8px;width:36%;vertical-align:top;"><table style="width:100%;border:none;"><tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Invoice No.</td><td style="padding:2px 0;border:none;">: ${order.invoice_number}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Dated</td><td style="padding:2px 0;border:none;">: ${dateStr}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Time</td><td style="padding:2px 0;border:none;">: ${timeStr}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Mode of Payment</td><td style="padding:2px 0;border:none;">: ${(order.credit_card !== 0 ? "Card" : order.credit_cash !== 0 ? "Cash" : order.credit_upi !== 0 ? "UPI" : "Pending").toUpperCase()}</td></tr><tr><td style="padding:2px 0;border:none;font-weight:bold;">Cashier</td><td style="padding:2px 0;border:none;">: ${userName || "Staff"}</td></tr>${order.scheduled_delivery_date ? `<tr><td style="padding:2px 0;border:none;font-weight:bold;white-space:nowrap;">Delivery Date</td><td style="padding:2px 0;border:none;">: ${fmtDate(order.scheduled_delivery_date)}</td></tr>` : ""}</table></td></tr><tr><td style="width:32%;border-right:1px solid #000;border-bottom:1px solid #000;padding:8px;">${buyerBlock("Consignee (Ship to)")}</td><td style="width:32%;border-right:1px solid #000;border-bottom:1px solid #000;padding:8px;">${buyerBlock("Buyer (Bill to)")}</td><td style="width:32%;padding:8px;font-size:0.8rem;">For Order Inquiry: ${order.referred_by_staff_name || "—"}</td></tr></table>
          <table class="outer-border" style="border-top:none;margin-top:-1px;"><thead><tr style="background:#f0f0f0;border-bottom:1px solid #000;"><th style="border-right:1px solid #aaa;width:4%;text-align:center;">Sl<br/>No.</th><th style="border-right:1px solid #aaa;width:27%;text-align:left;">Description of Goods</th><th style="border-right:1px solid #aaa;width:8%;text-align:center;">HSN/SAC</th><th style="border-right:1px solid #aaa;width:8%;text-align:center;">Quantity</th><th style="border-right:1px solid #aaa;width:10%;text-align:right;">Rate</th><th style="border-right:1px solid #aaa;width:5%;text-align:center;">Per</th><th style="border-right:1px solid #aaa;width:8%;text-align:center;">Disc.%</th><th style="border-right:1px solid #aaa;width:12%;text-align:right;">Saved</th><th style="width:12%;text-align:right;">Amount</th></tr></thead><tbody>${itemRowsHTML}<tr style="font-weight:bold;border-top:1px solid #000;"><td colspan="3" style="text-align:right;">Total Quantity</td><td style="text-align:center;">${totalQuantity} Pcs</td><td colspan="5"></td></tr></tbody></table>
          <div style="text-align:right;font-weight:bold;color:#15803d;padding:5px 6px;border:1px solid #000;border-top:none;">You Saved on Items: ₹${itemDiscountTotal.toFixed(2)}</div>
  <table class="outer-border" style="margin-top: -1px; border-top: none">
    <tr>
      <td style="width: 62%; border-right: 1px solid #000" rowspan="3">
        <div style="font-weight: bold; margin-bottom: 4px">
          Amount Chargeable (in words)
        </div>
        <div style="font-style: italic">${amtWords}</div>
        ${
          isPendingPartial
            ? `
        <div style="margin-top: 6px; font-size: 10px; border-top:1px dashed #ccc; padding-top:5px;">
          ${(order.credit_cash || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>💵 Cash Paid:</span><span style="font-weight:bold;">₹${(order.credit_cash || 0).toFixed(2)}</span></div>` : ""}
          ${(order.credit_card || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>💳 Card Paid:</span><span style="font-weight:bold;">₹${(order.credit_card || 0).toFixed(2)}</span></div>` : ""}
          ${(order.credit_upi || 0) > 0 ? `<div style="display:flex;justify-content:space-between;"><span>📲 UPI Paid:</span><span style="font-weight:bold;">₹${(order.credit_upi || 0).toFixed(2)}</span></div>` : ""}
          <div style="display:flex;justify-content:space-between;font-weight:bold;color:#15803d;border-top:1px solid #ddd;margin-top:2px;padding-top:2px;"><span>✅ Total Paid:</span><span>₹${computedReceived.toFixed(2)}</span></div>
          <div style="display:flex;justify-content:space-between;font-weight:bold;color:#dc2626;"><span>⚠ Balance Due:</span><span>₹${computedBalance.toFixed(2)}</span></div>
        </div>
        `
            : ""
        }
        <div style="font-size: 9px; margin-top: 4px; color: #555">
          E. &amp; O.E
        </div>
      </td>
      ${
        Math.abs(roundOff) >= 0.01
          ? `
      <td
        style="
          text-align: right;
          border-right: 1px solid #000;
          padding: 3px 6px;
          width: 26%;
        "
      >
        Less : Round Off
      </td>
      <td style="text-align: right; padding: 3px 6px; width: 12%">
        ${roundOff < 0 ? "(-)" : "(+)"} ₹${Math.abs(roundOff).toFixed(2)}
      </td>
      `
          : `
      <td style="border: none" colspan="2"></td>
      `
      }
    </tr>
    ${
      transport > 0
        ? `
    <tr>
      <td style="text-align:right;border-right:1px solid #000;border-top:1px solid #ddd;padding:3px 6px;color:#2563eb;">
        🚚 Transport Charges
      </td>
      <td style="text-align:right;border-top:1px solid #ddd;padding:3px 6px;color:#2563eb;">
        + ₹${transport.toFixed(2)}
      </td>
    </tr>`
        : ""
    }
    <tr>
      <td
        style="
          text-align: right;
          font-weight: bold;
          font-size: 12px;
          border-right: 1px solid #000;
          border-top: 1px solid #000;
          padding: 4px 0px;
        "
      >
        <div style="border-bottom: 1px solid #000;pdding-right:2px;">Total Before Discount</div>
        <div style="border-bottom: 1px solid #000; pdding-right:2px;">Discount Amount</div>
        <div style="border-bottom: 1px solid #000; pdding-right:2px;">Total After Discount</div>
      </td>
      <td
        style="
          text-align: right;
          font-weight: bold;
          font-size: 12px;
          border-top: 1px solid #000;
          padding: 4px 0px;
        "
      >
        <div style="border-bottom: 1px solid #000;pdding-right:2px;">₹ ${totalBeforeItemDiscount.toFixed(2)}</div>
        <div style="border-bottom: 1px solid #000;pdding-right:2px;">${itemDiscountTotal > 0 || order.discount > 0 || extraDisc > 0 ? `<span style=" font-size: 8px;font-weight: bold;">Item Discount: ₹${itemDiscountTotal.toFixed(2)}${order.discount > 0 ? ` + Order: ₹${(order.discount || 0).toFixed(2)}` : ""}${extraDisc > 0 ? ` + Extra: ₹${extraDisc.toFixed(2)}` : ""}</span>` : "&nbsp;"}</div>
        <div style="border-bottom: 1px solid #000;pdding-right:2px;">₹ ${(order.total || 0).toFixed(2)}</div>
        </td>
    </tr> 
  </table>
    ${hsnTableHTML}
    <div class="outer-border"style="border-top:none;padding:5px;font-size:10px;font-style:italic;">Tax Amount (in words): <b>${taxWords}</b></div>
    <table class="outer-border" style="margin-top:8px;"><tr><td style="width:34%;border-right:1px solid #000;vertical-align:top;padding:6px;"><div style="font-size:10px;font-weight:bold;">Company's PAN: ${COMPANY_SETTINGS.pan}</div><div style="font-size:10px;font-weight:bold;margin-top:8px;">Declaration</div><div style="font-size:9px;margin-top:3px;line-height:1.5;">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div></td><td style="width:33%;border-right:1px solid #000;vertical-align:top;padding:6px;"><div style="font-size:10px;font-weight:bold;">Company's Bank Details</div><table style="margin-top:4px;font-size:10px;line-height:1.8;"><tr><td style="border:none;padding:0;white-space:nowrap;font-weight:bold;">Bank Name</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankName}</td></tr><tr><td style="border:none;padding:0;font-weight:bold;">A/c No.</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankAccount}</td></tr><tr><td style="border:none;padding:0;font-weight:bold;white-space:nowrap;">Branch &amp; IFS Code</td><td style="border:none;padding:0 0 0 6px;">: ${COMPANY_SETTINGS.bankBranch} &amp; ${COMPANY_SETTINGS.bankIFSC}</td></tr></table></td><td style="width:33%;text-align:center;vertical-align:bottom;padding:6px;"><div style="font-size:10px;font-weight:bold;">for ${COMPANY_SETTINGS.name}</div><div style="margin-top:32px;border-top:1px solid #000;padding-top:4px;font-size:10px;">Authorised Signatory</div></td></tr></table>
    <div style="text-align:center;margin-top:6px;font-size:9px;color:#555;border-top:1px solid #ccc;padding-top:4px;">SUBJECT TO AHMEDABAD JURISDICTION &nbsp;|&nbsp; This is a Computer Generated Invoice</div>
    </body></html>`;
};

const printInvoice = (order, userName) => {
  const isGST = order?.invoice_type === "gst" && order?.gst_type;
  const html = isGST
    ? buildGSTInvoice(order, userName)
    : buildNonGSTInvoice(order, userName);
  if (!html) return;
  const win = window.open("", "_blank", "width=850,height=900,scrollbars=yes");
  if (!win) {
    toast.error("Pop-up blocked! Please allow pop-ups.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
    setTimeout(() => win.close(), 600);
  };
};

const buildCreditNoteDocument = (note) =>
  `<!doctype html><html><head><meta charset="utf-8"><title>${note.credit_note_number}</title><style>body{font-family:Arial,sans-serif;color:#111;max-width:800px;margin:0 auto;padding:32px}h1{text-align:center;margin-bottom:4px}h2{border-bottom:1px solid #ddd;padding-bottom:8px}table{width:100%;border-collapse:collapse;margin:18px 0}th,td{border:1px solid #ddd;padding:9px;text-align:left}th{background:#f5f5f5}.right{text-align:right}.summary{margin-left:auto;width:300px}.note{margin-top:24px;padding:12px;background:#fff8e8;border:1px solid #e4b84a}@media print{body{padding:0}@page{margin:14mm}}</style></head><body><h1>${COMPANY_SETTINGS.name}</h1><div style="text-align:center">${COMPANY_SETTINGS.address} · ${COMPANY_SETTINGS.phone}</div><h2>Credit Note ${note.credit_note_number}</h2><p><b>Date:</b> ${note.created_at ? new Date(note.created_at).toLocaleDateString("en-IN") : "—"}<br><b>Customer:</b> ${note.customer_name || "—"} (${note.customer_phone || "—"})<br><b>Original Order:</b> ${note.original_invoice_number}<br><b>Original Purchase Date:</b> ${note.original_purchase_date ? new Date(note.original_purchase_date).toLocaleDateString("en-IN") : "—"}</p><table><thead><tr><th>Returned Item</th><th>Quantity</th><th>Original Price</th><th class="right">Amount</th></tr></thead><tbody>${(note.returned_items || []).map((item) => `<tr><td>${item.product_name}</td><td>${item.quantity}</td><td>₹${Number(item.price || 0).toFixed(2)}</td><td class="right">₹${Number(item.total || 0).toFixed(2)}</td></tr>`).join("")}</tbody></table><table class="summary"><tr><td>Credit Issued</td><td class="right">₹${Number(note.credit_issued || 0).toFixed(2)}</td></tr><tr><td>Credit Used</td><td class="right">₹${Number(note.credit_used || 0).toFixed(2)}</td></tr><tr><td><b>Remaining Credit</b></td><td class="right"><b>₹${Number(note.remaining_credit || 0).toFixed(2)}</b></td></tr><tr><td>Status</td><td class="right"><b>${note.status}</b></td></tr></table><div class="note">Remaining credit can be used for future purchases by this customer.</div><h2>Usage History</h2><table><thead><tr><th>Date</th><th>New Order</th><th>Credit Used</th><th>Remaining</th></tr></thead><tbody>${(note.usage_history || []).map((u) => `<tr><td>${u.used_at ? new Date(u.used_at).toLocaleDateString("en-IN") : "—"}</td><td>${u.order_number || u.order_id || "—"}</td><td>₹${Number(u.amount || 0).toFixed(2)}</td><td>₹${Number(u.remaining_credit || 0).toFixed(2)}</td></tr>`).join("") || `<tr><td colspan="4">No usage yet.</td></tr>`}</tbody></table></body></html>`;

const printCreditNote = (note) => {
  const win = window.open("", "_blank", "width=850,height=900,scrollbars=yes");
  if (!win) return toast.error("Pop-up blocked! Please allow pop-ups.");
  win.document.open();
  win.document.write(buildCreditNoteDocument(note));
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
  };
};

const shareCreditNote = async (note) => {
  if (!navigator.share)
    return toast.info("Sharing is not supported in this browser");
  try {
    await navigator.share({
      title: note.credit_note_number,
      text: `${note.credit_note_number} · Credit issued ₹${Number(note.credit_issued || 0).toFixed(2)} · Remaining ₹${Number(note.remaining_credit || 0).toFixed(2)}`,
    });
  } catch (_) {}
};

// ─── Delivery Status Badge ─────────────────────────────────────
const DeliveryBadge = ({
  status,
  scheduledDate,
  balanceDue,
  small = false,
}) => {
  const sz = small ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2.5 py-1";
  const ic = small ? 9 : 11;

  if (status === "delivered") {
    return (
      <span
        className={`inline-flex items-center gap-1 ${sz} font-black rounded-full border bg-emerald-500/15 text-emerald-400 border-emerald-500/20`}
      >
        <CheckCircle2 size={ic} /> Delivered
      </span>
    );
  }
  if (status === "pending_delivery") {
    const overdue = scheduledDate && isPast(scheduledDate);
    const today = scheduledDate && isToday(scheduledDate);
    return (
      <span
        className={`inline-flex items-center gap-1 ${sz} font-black rounded-full border ${overdue ? "bg-rose-500/15 text-rose-400 border-rose-500/20" : today ? "bg-orange-500/15 text-orange-400 border-orange-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}
      >
        <Clock size={ic} />{" "}
        {overdue ? "Overdue" : today ? "Due Today" : "Pending Pickup"}
      </span>
    );
  }
  if (status === "partial") {
    if (balanceDue !== undefined && balanceDue <= 0.01) {
      return (
        <span
          className={`inline-flex items-center gap-1 ${sz} font-black rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/20`}
        >
          <Clock size={ic} /> Pending Pickup
        </span>
      );
    }
    const overdue = scheduledDate && isPast(scheduledDate);
    const today = scheduledDate && isToday(scheduledDate);
    return (
      <span
        className={`inline-flex items-center gap-1 ${sz} font-black rounded-full border ${overdue ? "bg-rose-500/15 text-rose-400 border-rose-500/20" : today ? "bg-orange-500/15 text-orange-400 border-orange-500/20" : "bg-pink-500/15 text-pink-400 border-pink-500/20"}`}
      >
        <IndianRupee size={ic} />{" "}
        {overdue ? "Overdue+Bal" : today ? "Due Today+Bal" : "Balance Due"}
      </span>
    );
  }
  return null;
};

// ─── Product Search Panel ──────────────────────────────────────
const ProductSearchPanel = ({ token, existingProductIds, onAdd, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quantities, setQuantities] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/products?search=${encodeURIComponent(query)}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setResults(data.products || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, token]);

  const setQty = (id, val) =>
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(1, parseInt(val) || 1),
    }));

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="mt-3 bg-[#0f0f1e] border border-amber-500/20 rounded-2xl overflow-hidden shadow-xl"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/8 bg-amber-500/5">
        <Search size={13} className="text-amber-400 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search product by name or SKU…"
          className="flex-1 bg-transparent text-white text-xs outline-none placeholder-stone-600"
        />
        {loading && (
          <Loader2 size={12} className="text-amber-400 animate-spin shrink-0" />
        )}
        <button
          onClick={onClose}
          className="text-stone-500 hover:text-white ml-1 shrink-0 transition-colors"
        >
          <X size={13} />
        </button>
      </div>
      {results.length > 0 && (
        <div className="max-h-52 overflow-y-auto divide-y divide-white/5">
          {results.map((p) => {
            const alreadyIn = existingProductIds.includes(p.id);
            return (
              <div
                key={p.id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate">
                    {p.name}
                  </p>
                  <p className="text-[10px] text-stone-400">
                    ₹{p.selling_price}
                    <span className="mx-1 text-stone-600">·</span>
                    <span
                      className={`font-semibold ${p.stock_quantity > 5 ? "text-emerald-400" : p.stock_quantity > 0 ? "text-amber-400" : "text-rose-400"}`}
                    >
                      Stock: {p.stock_quantity}
                    </span>
                    {alreadyIn && (
                      <span className="ml-2 text-blue-400">• In order</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center bg-white/10 border border-white/15 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQty(p.id, (quantities[p.id] || 1) - 1)}
                      className="px-2 py-1 text-stone-400 hover:text-white hover:bg-white/10 transition-colors text-xs"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={p.stock_quantity || 999}
                      value={quantities[p.id] || 1}
                      onChange={(e) => setQty(p.id, e.target.value)}
                      className="w-10 bg-transparent text-xs text-white text-center outline-none py-1"
                    />
                    <button
                      onClick={() => setQty(p.id, (quantities[p.id] || 1) + 1)}
                      className="px-2 py-1 text-stone-400 hover:text-white hover:bg-white/10 transition-colors text-xs"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => onAdd(p, quantities[p.id] || 1)}
                    disabled={p.stock_quantity <= 0}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-black transition-all disabled:opacity-40 disabled:cursor-not-allowed ${alreadyIn ? "bg-blue-500/20 text-blue-400 border border-blue-500/25 hover:bg-blue-500/30" : "bg-amber-500 hover:bg-amber-400 text-black"}`}
                  >
                    {alreadyIn ? "+More" : "Add"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {query.trim() && !loading && results.length === 0 && (
        <div className="px-4 py-4 text-center text-stone-500 text-xs">
          No products found for "{query}"
        </div>
      )}
      {!query.trim() && (
        <div className="px-4 py-4 text-center text-stone-600 text-xs">
          Start typing to search products…
        </div>
      )}
    </motion.div>
  );
};

// ─── Editable field ────────────────────────────────────────────
const Field = ({
  label,
  value,
  name,
  onChange,
  type = "text",
  options,
  prefix,
  disabled = false,
}) => (
  <div>
    <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
      {label}
    </p>
    {options ? (
      <div className="relative">
        <select
          name={name}
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          className={`w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-amber-500/60 appearance-none pr-8 transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed bg-white/5" : ""
          }`}
        >
          {options.map((o) => (
            <option
              key={o.value}
              value={o.value}
              className="bg-[#1c1c2e] text-white"
            >
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={11}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
        />
      </div>
    ) : (
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
            {prefix}
          </span>
        )}
        <input
          type={type}
          name={name}
          value={value != null ? value : ""}
          onChange={onChange}
          disabled={disabled}
          className={`w-full bg-white/10 border border-white/20 rounded-xl py-2 text-xs text-white outline-none focus:border-amber-500/60 placeholder-stone-600 transition-colors ${
            prefix ? "pl-6 pr-3" : "px-3"
          } ${disabled ? "opacity-50 cursor-not-allowed bg-white/5" : ""}`}
        />
      </div>
    )}
  </div>
);

// ─── e-Way Bill Modal ──────────────────────────────────────────
const EWayBillModal = ({ order, onClose }) => {
  const [form, setForm] = useState({
    ewb_number: "",
    distance: "",
    valid_upto: "",
    vehicle_no: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1a1a2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/15 rounded-xl border border-blue-500/20">
              <FileText size={17} className="text-blue-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                e-Way Bill
              </p>
              <p className="text-white font-black text-sm">
                Invoice #{order.invoice_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["e-Way Bill No.", "ewb_number", "e.g. 682061598621"],
              ["Distance (KM)", "distance", "e.g. 1569"],
              ["Valid Upto", "valid_upto", "", "date"],
              ["Vehicle No.", "vehicle_no", "e.g. GJ01XX1234"],
            ].map(([label, key, ph, type]) => (
              <div key={key}>
                <label className="text-[10px] text-stone-500 uppercase tracking-widest mb-1 block font-bold">
                  {label}
                </label>
                <input
                  type={type || "text"}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={ph}
                  className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm outline-none focus:ring-2 focus:ring-amber-500/40 placeholder-stone-600"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => toast.info("e-Way Bill print triggered")}
            className="flex-1 h-11 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-sm flex items-center justify-center gap-2 transition-all"
          >
            <Printer size={15} /> Print e-Way Bill
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Schedule Delivery Modal ───────────────────────────────────
const ScheduleDeliveryModal = ({ order, onClose, onScheduled, token }) => {
  const [scenario, setScenario] = useState("full_paid");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [amountPaid, setAmountPaid] = useState(
    getReceivedAndBalance(order || {}).received || order?.total || 0,
  );
  const [paymentMode, setPaymentMode] = useState("cash");
  const [creditBreakdown, setCreditBreakdown] = useState({
    cash: "",
    card: "",
    upi: "",
  });
  const [saving, setSaving] = useState(false);

  const balanceDue = useMemo(() => {
    if (scenario === "full_paid") return 0;
    return Math.max(0, (order?.total || 0) - parseFloat(amountPaid || 0));
  }, [scenario, amountPaid, order?.total]);

  const handleSchedule = async () => {
    if (!deliveryDate) {
      toast.error("Please select a delivery date");
      return;
    }
    if (scenario === "partial_paid" && parseFloat(amountPaid) <= 0) {
      toast.error("Amount paid must be greater than 0");
      return;
    }
    const isFull = scenario === "full_paid";
    const actualAmountPaid = isFull
      ? order?.total || 0
      : parseFloat(amountPaid || 0);

    const cCash =
      paymentMode === "split"
        ? parseFloat(creditBreakdown.cash) || 0
        : paymentMode === "cash"
          ? actualAmountPaid
          : 0;
    const cCard =
      paymentMode === "split"
        ? parseFloat(creditBreakdown.card) || 0
        : paymentMode === "card"
          ? actualAmountPaid
          : 0;
    const cUpi =
      paymentMode === "split"
        ? parseFloat(creditBreakdown.upi) || 0
        : paymentMode === "upi"
          ? actualAmountPaid
          : 0;

    if (
      paymentMode === "split" &&
      Math.abs(cCash + cCard + cUpi - actualAmountPaid) > 0.01
    ) {
      toast.error(
        `Payment breakdown (₹${(cCash + cCard + cUpi).toFixed(2)}) must exactly match Amount Paid Now (₹${actualAmountPaid.toFixed(2)})`,
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        status: scenario === "full_paid" ? "pending_delivery" : "partial",
        scheduled_delivery_date: deliveryDate,
        amount_received: actualAmountPaid,
        balance_due: balanceDue,
        payment_mode: paymentMode === "split" ? "credit" : paymentMode,
        credit_cash: cCash,
        credit_card: cCard,
        credit_upi: cUpi,
      };
      await axios.put(`${API_URL}/orders/${order.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = { ...order, ...payload };
      onScheduled(updated);
      toast.success(`Delivery scheduled for ${fmtDate(deliveryDate)}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to schedule delivery");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1a1a2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-xl border border-amber-500/20">
              <Calendar size={17} className="text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                Schedule Delivery
              </p>
              <p className="text-white font-black text-sm">
                #{order?.invoice_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-2 font-bold">
              Payment Scenario
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setScenario("full_paid")}
                className={`p-3 rounded-2xl border text-left transition-all ${scenario === "full_paid" ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2
                    size={14}
                    className={
                      scenario === "full_paid"
                        ? "text-amber-400"
                        : "text-stone-500"
                    }
                  />
                  <span className="text-xs font-black text-white">
                    Full Payment
                  </span>
                </div>
                <p className="text-[10px] text-stone-400 leading-relaxed">
                  Customer paid full amount. Goods will be picked up later.
                </p>
              </button>
              <button
                onClick={() => setScenario("partial_paid")}
                className={`p-3 rounded-2xl border text-left transition-all ${scenario === "partial_paid" ? "border-pink-500/50 bg-pink-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <IndianRupee
                    size={14}
                    className={
                      scenario === "partial_paid"
                        ? "text-pink-400"
                        : "text-stone-500"
                    }
                  />
                  <span className="text-xs font-black text-white">
                    Partial Payment
                  </span>
                </div>
                <p className="text-[10px] text-stone-400 leading-relaxed">
                  Part paid now, rest collected on delivery.
                </p>
              </button>
            </div>
          </div>

          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
              Scheduled Delivery / Pickup Date
            </p>
            <input
              type="date"
              value={deliveryDate}
              min={todayStr()}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500/60 transition-colors"
            />
          </div>

          {scenario === "partial_paid" && (
            <div className="space-y-3">
              <div>
                <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
                  Amount Paid Now (₹)
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={order?.total || 999999}
                    step="0.01"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl pl-6 pr-3 py-2.5 text-sm text-white outline-none focus:border-pink-500/60 font-mono transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2.5 text-center">
                  <p className="text-[10px] text-emerald-400 uppercase font-bold mb-0.5">
                    Paid Now
                  </p>
                  <p className="font-mono font-black text-emerald-400 text-sm">
                    ₹{parseFloat(amountPaid || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2.5 text-center">
                  <p className="text-[10px] text-rose-400 uppercase font-bold mb-0.5">
                    Due on Delivery
                  </p>
                  <p className="font-mono font-black text-rose-400 text-sm">
                    ₹{balanceDue.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="bg-white/5 border border-white/8 rounded-xl px-3 py-2 flex justify-between items-center">
                <span className="text-xs text-stone-400">Order Total</span>
                <span className="font-mono font-black text-white text-sm">
                  ₹{(order?.total || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
              Payment Mode (For Amount Paid Now)
            </p>
            <div className="flex flex-wrap gap-2">
              {["cash", "card", "upi", "split"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setPaymentMode(mode)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-colors ${paymentMode === mode ? "bg-emerald-500 text-white" : "bg-white/10 text-stone-400 hover:bg-white/20"}`}
                >
                  {mode}
                </button>
              ))}
            </div>

            {paymentMode === "split" && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { key: "cash", label: "Cash" },
                  { key: "card", label: "Card" },
                  { key: "upi", label: "UPI" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <p className="text-[10px] text-stone-400 uppercase mb-1">
                      {label}
                    </p>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={creditBreakdown[key]}
                      onChange={(e) =>
                        setCreditBreakdown((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-2 py-2 text-xs text-white outline-none focus:border-emerald-500/60 font-mono"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {scenario === "full_paid" && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
              <AlertCircle
                size={14}
                className="text-amber-400 mt-0.5 shrink-0"
              />
              <p className="text-xs text-amber-300">
                Full amount of{" "}
                <span className="font-mono font-bold">
                  ₹{(order?.total || 0).toFixed(2)}
                </span>{" "}
                is marked as received. Goods will be held until pickup date.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={saving}
            className="flex-1 h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Calendar size={15} />
            )}
            Schedule Delivery
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Collect Balance Modal ─────────────────────────────────────
// const CollectBalanceModal = ({ order, onClose, onCollected, token }) => {
//   const [paymentMode, setPaymentMode] = useState("cash");
//   const [saving, setSaving] = useState(false);
//   const balanceDue = order?.credit_amount || order?.balance_due || 0;

//   const handleCollect = async () => {
//     setSaving(true);
//     try {
//       const payload = {
//         status: "delivered",
//         amount_received: (order?.amount_received || 0) + balanceDue,
//         balance_due: 0,
//         credit_amount: 0,
//       };
//       await axios.put(`${API_URL}/orders/${order.id}`, payload, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       onCollected({ ...order, ...payload });
//       toast.success(
//         `Balance of ₹${balanceDue.toFixed(2)} collected. Order delivered!`,
//       );
//       onClose();
//     } catch (err) {
//       toast.error(err?.response?.data?.detail || "Failed to update");
//     } finally {
//       setSaving(false);
//     }
//   };

//   return (
//     <motion.div
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//       className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
//       onClick={onClose}
//     >
//       <motion.div
//         initial={{ scale: 0.93, y: 16 }}
//         animate={{ scale: 1, y: 0 }}
//         exit={{ scale: 0.93, y: 16 }}
//         transition={{ type: "spring", stiffness: 340, damping: 30 }}
//         className="bg-[#1a1a2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
//         onClick={(e) => e.stopPropagation()}
//       >
//         <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
//           <div className="flex items-center gap-3">
//             <div className="p-2 bg-emerald-500/15 rounded-xl border border-emerald-500/20">
//               <IndianRupee size={17} className="text-emerald-400" />
//             </div>
//             <div>
//               <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
//                 Collect Balance & Deliver
//               </p>
//               <p className="text-white font-black text-sm">
//                 #{order?.invoice_number}
//               </p>
//             </div>
//           </div>
//           <button
//             onClick={onClose}
//             className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
//           >
//             <X size={16} />
//           </button>
//         </div>
//         <div className="px-6 py-5 space-y-4">
//           <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4 text-center">
//             <p className="text-[10px] text-rose-400 uppercase font-bold mb-1">
//               Balance Due from Customer
//             </p>
//             <p className="font-mono font-black text-rose-400 text-3xl">
//               ₹{balanceDue.toFixed(2)}
//             </p>
//             <p className="text-[11px] text-stone-400 mt-1">
//               Previously paid: ₹{(order?.amount_received || 0).toFixed(2)}
//             </p>
//           </div>
//           <div>
//             <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
//               Payment Mode for Balance
//             </p>
//             <div className="relative">
//               <select
//                 value={paymentMode}
//                 onChange={(e) => setPaymentMode(e.target.value)}
//                 className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/60 appearance-none pr-8"
//               >
//                 <option value="cash" className="bg-[#1c1c2e]">
//                   Cash
//                 </option>
//                 <option value="card" className="bg-[#1c1c2e]">
//                   Card
//                 </option>
//                 <option value="upi" className="bg-[#1c1c2e]">
//                   UPI
//                 </option>
//               </select>
//               <ChevronDown
//                 size={11}
//                 className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
//               />
//             </div>
//           </div>
//           <div className="bg-white/5 border border-white/8 rounded-xl px-3 py-2.5 flex justify-between items-center">
//             <span className="text-xs text-stone-400">Total Order Value</span>
//             <span className="font-mono font-black text-white text-sm">
//               ₹{(order?.total || 0).toFixed(2)}
//             </span>
//           </div>
//         </div>
//         <div className="px-6 py-4 border-t border-white/10 flex gap-3">
//           <button
//             onClick={onClose}
//             className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
//           >
//             Cancel
//           </button>
//           <button
//             onClick={handleCollect}
//             disabled={saving}
//             className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
//           >
//             {saving ? (
//               <Loader2 size={15} className="animate-spin" />
//             ) : (
//               <CheckCircle2 size={15} />
//             )}
//             Collect & Deliver
//           </button>
//         </div>
//       </motion.div>
//     </motion.div>
//   );
// };
const CollectBalanceModal = ({
  order,
  onClose,
  onCollected,
  token,
  currentUserName,
}) => {
  const [paymentMode, setPaymentMode] = useState("cash");
  const [collectAmount, setCollectAmount] = useState("");
  const [saving, setSaving] = useState(false);

  // Compute true balance using same helper
  const { received: totalPaid, balance: trueBalance } =
    getReceivedAndBalance(order);
  const existingCollections = order?.payment_collections || [];

  const inputAmount = parseFloat(collectAmount) || 0;
  const remainingAfter = Math.max(0, trueBalance - inputAmount);
  const isFullSettlement = inputAmount > 0 && inputAmount >= trueBalance;

  const handleCollect = async () => {
    if (inputAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (inputAmount > trueBalance + 0.01) {
      toast.error("Amount exceeds balance due");
      return;
    }

    setSaving(true);
    try {
      const newEntry = {
        amount: inputAmount,
        mode: paymentMode,
        collected_at: new Date().toISOString(),
        collected_by: currentUserName || "Staff",
      };

      const updatedCollections = [...existingCollections, newEntry];

      // Recalculate from scratch
      const initialReceived =
        (order.credit_cash || 0) +
        (order.credit_card || 0) +
        (order.credit_upi || 0);
      const newTotalReceived =
        initialReceived + updatedCollections.reduce((s, c) => s + c.amount, 0);
      const newBalance = Math.max(0, (order.total || 0) - newTotalReceived);
      const newStatus = newBalance <= 0.01 ? "delivered" : "partial";

      const payload = {
        status: newStatus,
        amount_received: newTotalReceived,
        balance_due: newBalance,
        credit_amount: newBalance,
        payment_collections: updatedCollections,
      };

      await axios.put(`${API_URL}/orders/${order.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      onCollected({ ...order, ...payload });
      toast.success(
        newStatus === "delivered"
          ? `₹${inputAmount.toFixed(2)} collected — fully settled & delivered!`
          : `₹${inputAmount.toFixed(2)} collected — ₹${newBalance.toFixed(2)} still due`,
      );
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const fmtCollectedAt = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1a1a2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/15 rounded-xl border border-emerald-500/20">
              <IndianRupee size={17} className="text-emerald-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                Collect Balance Payment
              </p>
              <p className="text-white font-black text-sm">
                #{order?.invoice_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Balance summary cards */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white/5 border border-white/8 rounded-xl px-2 py-2.5">
              <p className="text-[9px] text-stone-500 uppercase font-bold mb-0.5">
                Order Total
              </p>
              <p className="font-mono font-black text-white text-sm">
                ₹{(order?.total || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-2 py-2.5">
              <p className="text-[9px] text-emerald-400 uppercase font-bold mb-0.5">
                Total Paid
              </p>
              <p className="font-mono font-black text-emerald-400 text-sm">
                ₹{totalPaid.toFixed(2)}
              </p>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl px-2 py-2.5">
              <p className="text-[9px] text-rose-400 uppercase font-bold mb-0.5">
                Balance Due
              </p>
              <p className="font-mono font-black text-rose-400 text-sm">
                ₹{trueBalance.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Payment History */}
          <div>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
              <Clock size={10} /> Payment History
            </p>
            <div className="space-y-1.5">
              {/* Initial payment row */}
              <div className="flex items-center justify-between bg-white/5 border border-white/8 rounded-xl px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-white">
                    Initial Payment ·{" "}
                    <span className="capitalize text-stone-400">
                      {getPaymentLabel(order)}
                    </span>
                  </p>
                  <p className="text-[10px] text-stone-500">
                    {fmtCollectedAt(order?.created_at)}
                  </p>
                </div>
                <p className="font-mono font-black text-emerald-400 text-sm">
                  ₹
                  {(
                    (order?.credit_cash || 0) +
                    (order?.credit_card || 0) +
                    (order?.credit_upi || 0)
                  ).toFixed(2)}
                </p>
              </div>

              {/* Subsequent collections */}
              {existingCollections.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/15 rounded-xl px-3 py-2"
                >
                  <div>
                    <p className="text-xs font-semibold text-white capitalize">
                      Collection {i + 1} · {c.mode}
                    </p>
                    <p className="text-[10px] text-stone-500">
                      {fmtCollectedAt(c.collected_at)}
                      {c.collected_by && (
                        <span className="ml-1">· {c.collected_by}</span>
                      )}
                    </p>
                  </div>
                  <p className="font-mono font-black text-emerald-400 text-sm">
                    ₹{(c.amount || 0).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Collect input — only show if balance remaining */}
          {trueBalance > 0.01 ? (
            <>
              <div>
                <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
                  Amount to Collect Now
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={trueBalance}
                    step="0.01"
                    value={collectAmount}
                    onChange={(e) => setCollectAmount(e.target.value)}
                    placeholder={`Max ₹${trueBalance.toFixed(2)}`}
                    className="w-full bg-white/10 border border-white/20 rounded-xl pl-6 pr-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/60 font-mono transition-colors"
                  />
                </div>
                {/* Quick fill */}
                <div className="flex gap-1.5 mt-2">
                  {trueBalance / 2 > 0.01 && (
                    <button
                      onClick={() =>
                        setCollectAmount((trueBalance / 2).toFixed(2))
                      }
                      className="flex-1 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[10px] font-bold text-stone-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      Half ₹{(trueBalance / 2).toFixed(2)}
                    </button>
                  )}
                  <button
                    onClick={() => setCollectAmount(trueBalance.toFixed(2))}
                    className="flex-1 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[10px] font-bold text-stone-400 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    Full ₹{trueBalance.toFixed(2)}
                  </button>
                </div>
              </div>

              <div>
                <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5 font-bold">
                  Payment Mode
                </p>
                <div className="relative">
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500/60 appearance-none pr-8"
                  >
                    <option value="cash" className="bg-[#1c1c2e]">
                      💵 Cash
                    </option>
                    <option value="card" className="bg-[#1c1c2e]">
                      💳 Card
                    </option>
                    <option value="upi" className="bg-[#1c1c2e]">
                      📲 UPI
                    </option>
                  </select>
                  <ChevronDown
                    size={11}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
                  />
                </div>
              </div>

              {/* Preview */}
              {inputAmount > 0 && (
                <div
                  className={`rounded-xl px-4 py-3 border ${
                    isFullSettlement
                      ? "bg-emerald-500/10 border-emerald-500/20"
                      : "bg-amber-500/10 border-amber-500/20"
                  }`}
                >
                  <p
                    className={`text-xs font-bold ${isFullSettlement ? "text-emerald-400" : "text-amber-400"}`}
                  >
                    {isFullSettlement
                      ? "✅ Full settlement — order will be marked Delivered"
                      : `⏳ Partial — ₹${remainingAfter.toFixed(2)} still due after this collection`}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-center">
              <p className="text-sm font-black text-emerald-400">
                ✅ Fully Paid — No balance due
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-white/10 text-stone-400 hover:text-white text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          {trueBalance > 0.01 && (
            <button
              onClick={handleCollect}
              disabled={saving || inputAmount <= 0}
              className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <CheckCircle2 size={15} />
              )}
              {isFullSettlement ? "Collect & Deliver" : "Record Payment"}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Order Modal ───────────────────────────────────────────────
const ReturnItemsModal = ({
  order,
  token,
  existingNotes = [],
  onClose,
  onUpdated,
}) => {
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const returnedByIndex = existingNotes.reduce((map, note) => {
    (note.returned_items || []).forEach((item) => {
      map[item.item_index] =
        (map[item.item_index] || 0) + Number(item.quantity || 0);
    });
    return map;
  }, {});
  const submit = async () => {
    const items = Object.entries(selected)
      .filter(([, quantity]) => quantity > 0)
      .map(([item_index, quantity]) => ({
        item_index: Number(item_index),
        quantity: Number(quantity),
      }));
    if (!items.length) return toast.error("Select at least one item to return");
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/orders/${order.id}/credit-notes`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Credit note created");
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Return failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-[#1c1c2e] border border-white/10 rounded-2xl p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-black">Return Items · Credit Note</h3>
          <button onClick={onClose}>
            <X className="text-stone-400" size={18} />
          </button>
        </div>
        <div className="space-y-2 max-h-72 overflow-auto">
          {(order.items || []).map((item, index) => {
            const available = Math.max(
              0,
              Number(item.quantity || 0) - (returnedByIndex[index] || 0),
            );
            return (
              <div
                key={index}
                className={`flex items-center gap-3 bg-white/5 rounded-xl p-3 ${available === 0 ? "opacity-50" : ""}`}
              >
                <input
                  type="checkbox"
                  disabled={available === 0}
                  checked={!!selected[index]}
                  onChange={(e) =>
                    setSelected((s) => ({
                      ...s,
                      [index]: e.target.checked ? 1 : 0,
                    }))
                  }
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-bold">
                    {item.product_name}
                  </p>
                  <p className="text-stone-400 text-xs">
                    {rupee(item.price)} · {available} available
                    {returnedByIndex[index]
                      ? ` · ${returnedByIndex[index]} returned`
                      : ""}
                  </p>
                </div>
                {selected[index] ? (
                  <input
                    type="number"
                    min="1"
                    max={available}
                    value={selected[index]}
                    onChange={(e) =>
                      setSelected((s) => ({
                        ...s,
                        [index]: Math.min(
                          available,
                          Math.max(1, Number(e.target.value) || 1),
                        ),
                      }))
                    }
                    className="w-16 rounded-lg bg-white/10 border border-white/10 text-white p-2 text-center"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        <button
          onClick={submit}
          disabled={saving}
          className="w-full mt-5 rounded-xl bg-amber-500 text-black py-3 font-black disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create Credit Note"}
        </button>
      </div>
    </div>
  );
};

const OrderModal = ({
  order,
  onClose,
  onUpdated,
  onDeleted,
  currentUserName,
  staffs,
  currentUserRole,
}) => {
  const { token } = useAuth();
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [markingDelivery, setMarkingDelivery] = useState(false);
  const [form, setForm] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [extraDiscount, setExtraDiscount] = useState(0);
  const [originalDiscount, setOriginalDiscount] = useState(0);
  const [manualRoundOff, setManualRoundOff] = useState(0);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEWay, setShowEWay] = useState(false);
  const [transportCharges, setTransportCharges] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showCollectBalance, setShowCollectBalance] = useState(false);
  const [showReturnItems, setShowReturnItems] = useState(false);
  const [orderCreditNotes, setOrderCreditNotes] = useState([]);
  console.log(form, "form data");
  useEffect(() => {
    if (!order?.id) return;
    axios
      .get(`${API_URL}/orders/${order.id}/credit-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setOrderCreditNotes(r.data || []))
      .catch(() => setOrderCreditNotes([]));
  }, [order?.id, token]);

  const [staffList, setStaffList] = useState(staffs ?? []);

  useEffect(() => {
    if (staffs && staffs.length > 0) {
      setStaffList(staffs);
    }
  }, [staffs]);

  useEffect(() => {
    if (order) {
      setForm({
        customer_name: order.customer_name || "",
        customer_phone: order.customer_phone || "",
        customer_gstin: order.customer_gstin || "",
        customer_address: order.customer_address || "",
        payment_mode: normalizePaymentMode(order.payment_mode),
        narration: order.narration || "",
        credit_cash: order.credit_cash ?? 0,
        credit_card: order.credit_card ?? 0,
        referred_by_staff_id: order.referred_by_staff_id || "",
        credit_upi: order.credit_upi ?? 0,
        amount_received: order.amount_received ?? order.total ?? 0,
        credit_amount: order.credit_amount ?? 0,
        scheduled_delivery_date: order.scheduled_delivery_date || "",
        google_review: order.google_review || "",
      });
      setTransportCharges(order.transport_charges || 0);
      setOriginalDiscount(order.discount || 0);

      setEditItems(order.items ? order.items.map((i) => ({ ...i })) : []);
      setExtraDiscount(order.extra_discount || 0);
      setManualRoundOff(order.round_off || 0);
      setDeliveryStatus(order.status || null);
      setMode("view");
      setShowAddProduct(false);
    }
  }, [order]);

  if (!order) return null;

  const isAdmin = ["admin"].includes(currentUserRole);
  const canEdit = ["admin", "billing"].includes(currentUserRole);

  const newSubtotal = useMemo(
    () =>
      (editItems || []).reduce(
        // Item price is already the product's discounted final price.
        // Product percentage discounts are informational here; only the
        // separate order-level discounts are applied below.
        (s, item) => s + Number(item.price || 0) * Number(item.quantity || 0),
        0,
      ),
    [editItems],
  );
  const totalItemQuantity = (order.items || []).reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );
  const newTotal = useMemo(() => {
    const safeTransport = parseFloat(transportCharges) || 0;
    const afterDiscount = newSubtotal - originalDiscount - extraDiscount;
    return Math.max(
      0,
      Math.round((afterDiscount + safeTransport + manualRoundOff) * 100) / 100,
    );
  }, [
    newSubtotal,
    originalDiscount,
    extraDiscount,
    transportCharges,
    manualRoundOff,
  ]);

  const derivedCreditAmount = useMemo(() => {
    if (form.payment_mode !== "credit") return 0;
    const received =
      (parseFloat(form.credit_cash) || 0) +
      (parseFloat(form.credit_card) || 0) +
      (parseFloat(form.credit_upi) || 0);
    return Math.max(0, (newTotal || 0) - received);
  }, [
    form.credit_cash,
    form.credit_card,
    form.credit_upi,
    form.payment_mode,
    newTotal,
  ]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm((f) => {
      const updated = {
        ...f,
        [name]: type === "number" ? parseFloat(value) || 0 : value,
      };
      if (name === "payment_mode" && ["split", "credit"].includes(value)) {
        const curSum =
          (parseFloat(f.credit_cash) || 0) +
          (parseFloat(f.credit_card) || 0) +
          (parseFloat(f.credit_upi) || 0);
        if (curSum === 0) {
          const initialPaid =
            parseFloat(order.amount_received) || parseFloat(order.total) || 0;
          const prevMode = normalizePaymentMode(order.payment_mode);
          if (prevMode === "card") updated.credit_card = initialPaid;
          else if (prevMode === "upi") updated.credit_upi = initialPaid;
          else updated.credit_cash = initialPaid;
        }
      }
      return updated;
    });
  };
  const updateItemQty = useCallback((index, qty) => {
    const safeQty = Math.max(1, parseInt(qty) || 1);
    setEditItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, quantity: safeQty, total: item.price * safeQty }
          : item,
      ),
    );
  }, []);

  const removeItem = useCallback((index) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateItemDiscount = useCallback((index, discount) => {
    const safeDiscount = Math.max(0, parseFloat(discount) || 0);
    setEditItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, discount: safeDiscount } : item,
      ),
    );
  }, []);

  const enrichedForPrint = (order) => ({
    ...order,
    referred_by_staff_name:
      staffList.find((s) => s.id === order.referred_by_staff_id)?.name || null,
  });

  const addProduct = useCallback((product, qty) => {
    const safeQty = Math.max(1, parseInt(qty) || 1);
    setEditItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.product_id === product.id,
      );
      if (existingIndex >= 0) {
        return prev.map((item, i) =>
          i === existingIndex
            ? {
                ...item,
                quantity: item.quantity + safeQty,
                total: item.price * (item.quantity + safeQty),
              }
            : item,
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: safeQty,
          price: product.selling_price,
          total: product.selling_price * safeQty,
          hsn: product.hsn || "",
          unit: "Nos",
          discount: 0,
          gst_rate: product.gst_rate || 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          taxable: 0,
        },
      ];
    });
    setShowAddProduct(false);
    toast.success(`${product.name} added to order`);
  }, []);

  const markDelivery = async (status) => {
    setMarkingDelivery(true);
    try {
      await axios.put(
        `${API_URL}/orders/${order.id}`,
        { status: status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDeliveryStatus(status);
      onUpdated({ ...order, status: status });
      toast.success(
        status === "delivered"
          ? "✅ Marked as delivered!"
          : status === "pending_delivery"
            ? "📦 Marked as pending pickup"
            : "Status updated",
      );
    } catch {
      toast.error("Failed to update delivery status");
    } finally {
      setMarkingDelivery(false);
    }
  };

  const handleSave = async () => {
    if (editItems.length === 0) {
      toast.error("Order must have at least one item");
      return;
    }

    const cCash = parseFloat(form.credit_cash) || 0;
    const cCard = parseFloat(form.credit_card) || 0;
    const cUpi = parseFloat(form.credit_upi) || 0;
    const breakdownSum = cCash + cCard + cUpi;

    if (form.payment_mode === "split") {
      if (Math.abs(breakdownSum - newTotal) > 0.01) {
        toast.error(
          `Split payment breakdown (₹${breakdownSum.toFixed(2)}) must match the order total (₹${newTotal.toFixed(2)})`,
        );
        return;
      }
    } else if (form.payment_mode === "credit") {
      if (breakdownSum > newTotal + 0.01) {
        toast.error(
          `Upfront payment (₹${breakdownSum.toFixed(2)}) cannot exceed the order total (₹${newTotal.toFixed(2)})`,
        );
        return;
      }
    }

    const actualAmountReceived =
      form.payment_mode === "credit"
        ? breakdownSum
        : form.payment_mode === "split"
          ? breakdownSum
          : newTotal;

    const actualBalanceDue =
      form.payment_mode === "credit"
        ? Math.max(0, newTotal - actualAmountReceived)
        : 0;

    const finalStatus =
      actualBalanceDue > 0
        ? "partial"
        : deliveryStatus === "partial"
          ? "delivered"
          : deliveryStatus || order.status || "delivered";

    setSaving(true);
    try {
      const payload = {
        ...form,
        items: editItems.map((item) => ({
          ...item,
          purchased: item.purchased === true,
          // Preserve the already-discounted item total. Do not subtract the
          // percentage value again.
          total: Number(item.price || 0) * Number(item.quantity || 0),
        })),
        transport_charges: transportCharges,
        subtotal: newSubtotal,
        total: newTotal,
        discount: originalDiscount,
        extra_discount: extraDiscount,
        round_off: manualRoundOff,
        referred_by_staff_id: form.referred_by_staff_id || "",
        status: finalStatus,
        payment_mode: form.payment_mode,
        credit_cash:
          form.payment_mode === "cash"
            ? newTotal
            : ["split", "credit"].includes(form.payment_mode)
              ? cCash
              : 0,
        credit_card:
          form.payment_mode === "card"
            ? newTotal
            : ["split", "credit"].includes(form.payment_mode)
              ? cCard
              : 0,
        credit_upi:
          form.payment_mode === "upi"
            ? newTotal
            : ["split", "credit"].includes(form.payment_mode)
              ? cUpi
              : 0,
        amount_received: actualAmountReceived,
        amount_paid: actualAmountReceived,
        balance_due: actualBalanceDue,
        credit_amount: actualBalanceDue,
        scheduled_delivery_date: form.scheduled_delivery_date || null,
      };
      await axios.put(`${API_URL}/orders/${order.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Order updated successfully");
      onUpdated({ ...order, ...payload });
      setMode("view");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Order deleted");
      onDeleted(order.id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleting(false);
    }
  };

  const displayOrder =
    mode === "edit"
      ? {
          ...order,
          ...form,
          extra_discount: extraDiscount,
          round_off: manualRoundOff,
        }
      : order;
  const hasGST = order.invoice_type === "gst" && order.tax > 0;

  // ── FIX: Correctly compute paid & balance from credit breakdown ──
  // amount_received may be 0 even when credit_upi/cash/card has values
  // const computedAmountReceived =
  //   order.amount_received > 0
  //     ? order.amount_received
  //     : (order.credit_cash || 0) +
  //       (order.credit_card || 0) +
  //       (order.credit_upi || 0);

  // const existingCollections = order?.payment_collections || [];
  const { received: computedAmountReceived, balance: computedBalanceDue } =
    getReceivedAndBalance(order);
  // const trueBalance = Math.max(0, (order?.total || 0) - totalCollected);

  const isPendingPartial = deliveryStatus === "partial";
  const isPending = deliveryStatus === "pending_delivery";
  const isScheduled = isPending || isPendingPartial;
  const schedDate = order.scheduled_delivery_date;
  const isOverdue = schedDate && isPast(schedDate);
  const isDueToday = schedDate && isToday(schedDate);
  const isCreditWithBalance =
    normalizePaymentMode(order.payment_mode) === "credit" &&
    computedBalanceDue > 0.01;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={() => mode === "view" && onClose()}
    >
      <motion.div
        initial={{ scale: 0.93, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.93, y: 16 }}
        transition={{ type: "spring", stiffness: 340, damping: 30 }}
        className="bg-[#1c1c2e] border border-white/10 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-xl border border-amber-500/20">
              <Receipt size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                Invoice
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-black font-mono text-sm">
                  {order.invoice_number}
                </p>
                <DeliveryBadge
                  status={deliveryStatus}
                  scheduledDate={schedDate}
                  balanceDue={computedBalanceDue}
                  small
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <button
              onClick={() =>
                printInvoice(enrichedForPrint(displayOrder), currentUserName)
              }
              title="Print"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-stone-700/60 hover:bg-stone-700 text-stone-300 hover:text-white text-[10px] font-bold border border-white/10 transition-all"
            >
              <Printer size={12} /> Print
            </button>
            {canEdit && mode === "view" && (
              <button
                onClick={() => setMode("edit")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 text-[10px] font-bold border border-blue-500/20 transition-all"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
            {mode === "view" && (
              <button
                onClick={() => setShowReturnItems(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-[10px] font-bold border border-amber-500/20 transition-all"
              >
                <RotateCcw size={12} /> Return
              </button>
            )}
            {mode === "edit" && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-[10px] font-bold border border-emerald-500/25 transition-all disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Save size={12} />
                  )}{" "}
                  Save
                </button>
                <button
                  onClick={() => {
                    setMode("view");
                    setShowAddProduct(false);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-stone-700/60 hover:bg-stone-700 text-stone-400 text-[10px] font-bold border border-white/10 transition-all"
                >
                  <RotateCcw size={12} /> Revert
                </button>
              </>
            )}
            {isAdmin && mode === "view" && (
              <button
                onClick={() => setMode("delete")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 text-[10px] font-bold border border-rose-500/20 transition-all"
              >
                <Trash2 size={12} />
              </button>
            )}
            {(order.total || 0) > 50000 &&
              order.customer_gstin &&
              mode === "view" && (
                <button
                  onClick={() => setShowEWay(true)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-blue-600/20 hover:bg-blue-600/35 text-blue-300 text-[10px] font-bold border border-blue-500/30 transition-all"
                >
                  <FileText size={12} /> e-Way
                </button>
              )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl hover:bg-white/10 text-stone-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        <AnimatePresence>
          {mode === "delete" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden flex-shrink-0"
            >
              <div className="mx-5 my-3 bg-rose-500/10 border border-rose-500/30 rounded-2xl px-5 py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    size={17}
                    className="text-rose-400 mt-0.5 shrink-0"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-rose-300">
                      Delete this order?
                    </p>
                    <p className="text-xs text-rose-400/70 mt-0.5">
                      This will permanently delete{" "}
                      <span className="font-mono font-bold">
                        {order.invoice_number}
                      </span>{" "}
                      and restore stock.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setMode("view")}
                    className="flex-1 py-2 rounded-xl bg-stone-700 hover:bg-stone-600 text-stone-300 text-xs font-bold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    {deleting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} />
                    )}{" "}
                    Yes, Delete
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* View: Meta + Delivery Status */}
          {mode === "view" && (
            <div className="px-5 py-4 border-b border-white/8 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                    Date & Time
                  </p>
                  <p className="text-white text-xs font-semibold">
                    {fmtFull(order.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                    Payment
                  </p>
                  <span
                    className={`text-[10px] font-black px-2.5 py-1 rounded-full border capitalize ${pmColor(getPaymentLabel(order))}`}
                  >
                    {getPaymentLabel(order)}
                  </span>
                </div>
                {order.customer_name && (
                  <div>
                    <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                      Customer
                    </p>
                    <p className="text-white text-xs font-semibold">
                      {order.customer_name}
                    </p>
                    {order.customer_phone && (
                      <p className="text-stone-400 text-[11px]">
                        {order.customer_phone}
                      </p>
                    )}
                  </div>
                )}
                {order.invoice_type && (
                  <div>
                    <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                      Type
                    </p>
                    <span className="text-[10px] font-black px-2.5 py-1 rounded-full border bg-stone-500/15 text-stone-400 border-stone-500/20 uppercase">
                      {order.invoice_type === "gst" ? "GST" : "Non-GST"}
                    </span>
                  </div>
                )}
              </div>

              {/* Delivery Status Panel */}
              <div
                className={`rounded-2xl px-4 py-3 space-y-3 ${isOverdue && isScheduled ? "bg-rose-500/8 border border-rose-500/20" : isDueToday && isScheduled ? "bg-orange-500/8 border border-orange-500/20" : "bg-white/5"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1.5">
                      Delivery Status
                    </p>
                    {/* Buttons removed first duplicate paid/balance grid — only show once below */}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1.5 flex-wrap shrink-0">
                      {markingDelivery ? (
                        <Loader2
                          size={16}
                          className="text-amber-400 animate-spin"
                        />
                      ) : (
                        <>
                          {!isScheduled && deliveryStatus !== "delivered" && (
                            <button
                              onClick={() => setShowSchedule(true)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-[10px] font-black border border-amber-500/20 transition-all"
                            >
                              <Calendar size={11} /> Schedule Delivery
                            </button>
                          )}
                          {isPending && (
                            <button
                              onClick={() => markDelivery("delivered")}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-[10px] font-black border border-emerald-500/20 transition-all"
                            >
                              <CheckCircle2 size={11} /> Mark Delivered
                            </button>
                          )}
                          {isPendingPartial && (
                            <button
                              onClick={() => setShowCollectBalance(true)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-[10px] font-black border border-emerald-500/20 transition-all"
                            >
                              <IndianRupee size={11} /> Collect Balance &
                              Deliver
                            </button>
                          )}
                          {deliveryStatus === "delivered" && (
                            <button
                              onClick={() => markDelivery(null)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-stone-700/60 hover:bg-stone-700 text-stone-400 text-[10px] font-bold border border-white/10 transition-all"
                            >
                              <RotateCcw size={10} /> Reset
                            </button>
                          )}
                          {isScheduled && deliveryStatus !== "delivered" && (
                            <button
                              onClick={() => setShowSchedule(true)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-stone-700/60 hover:bg-stone-700 text-stone-400 text-[10px] font-bold border border-white/10 transition-all"
                            >
                              <Pencil size={10} /> Edit
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Scheduled date info */}
                {schedDate && isScheduled && (
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isOverdue ? "bg-rose-500/10 border border-rose-500/20" : isDueToday ? "bg-orange-500/10 border border-orange-500/20" : "bg-white/5 border border-white/8"}`}
                  >
                    <Calendar
                      size={12}
                      className={
                        isOverdue
                          ? "text-rose-400"
                          : isDueToday
                            ? "text-orange-400"
                            : "text-stone-400"
                      }
                    />
                    <div>
                      <span
                        className={`text-xs font-bold ${isOverdue ? "text-rose-400" : isDueToday ? "text-orange-400" : "text-stone-300"}`}
                      >
                        {isOverdue
                          ? "⚠ Overdue — was due "
                          : isDueToday
                            ? "📅 Due Today — "
                            : "Scheduled for "}
                        {fmtDate(schedDate)}
                      </span>
                      {isOverdue && (
                        <span className="text-[10px] text-stone-500 ml-1">
                          (customer hasn't collected yet)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* FIX: Single paid/balance grid using computed values */}
                {isPendingPartial && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2 text-center">
                      <p className="text-[9px] text-emerald-400 uppercase font-bold mb-0.5">
                        Paid
                      </p>
                      <p className="font-mono font-black text-emerald-400 text-sm">
                        ₹{computedAmountReceived.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2 text-center">
                      <p className="text-[9px] text-rose-400 uppercase font-bold mb-0.5">
                        Balance Due
                      </p>
                      <p className="font-mono font-black text-rose-400 text-sm">
                        ₹{computedBalanceDue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit: Customer & Payment fields */}
          {mode === "edit" && (
            <div className="px-5 py-4 border-b border-white/8 space-y-3">
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                <User size={10} /> Customer & Payment
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Customer Name"
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                />
                <Field
                  label="Phone"
                  name="customer_phone"
                  value={form.customer_phone}
                  onChange={handleChange}
                  type="tel"
                />
              </div>
              <Field
                label="Address"
                name="customer_address"
                value={form.customer_address}
                onChange={handleChange}
              />
              {order.invoice_type === "gst" && (
                <Field
                  label="Customer GSTIN"
                  name="customer_gstin"
                  value={form.customer_gstin}
                  onChange={handleChange}
                />
              )}
              {/* If existing order is already credit with balance due, lock payment mode and upfront breakdown */}
              {isCreditWithBalance && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2 text-xs text-amber-300 flex items-center gap-2">
                  <span className="font-bold">
                    🔒 Credit Order with Active Balance:
                  </span>
                  <span>
                    Payment mode and upfront amounts cannot be modified. Collect
                    remaining balance via "Collect Balance" button.
                  </span>
                </div>
              )}
              <Field
                label="Payment Mode"
                name="payment_mode"
                value={form.payment_mode}
                onChange={handleChange}
                disabled={isCreditWithBalance}
                options={[
                  { value: "cash", label: "Cash" },
                  { value: "card", label: "Card" },
                  { value: "upi", label: "UPI" },
                  { value: "split", label: "Split" },
                  { value: "credit", label: "Credit" },
                ]}
              />
              {(form.payment_mode === "credit" ||
                form.payment_mode === "split") && (
                <div className="pt-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">
                      Payment Breakdown (upfront received)
                    </p>
                    {isCreditWithBalance && (
                      <span className="text-[10px] text-amber-400 font-semibold">
                        🔒 Locked (Balance Due: ₹{computedBalanceDue.toFixed(2)}
                        )
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        label: "💵 Cash",
                        name: "credit_cash",
                        color: "focus:border-emerald-500/60",
                      },
                      {
                        label: "💳 Card",
                        name: "credit_card",
                        color: "focus:border-blue-500/60",
                      },
                      {
                        label: "📲 UPI",
                        name: "credit_upi",
                        color: "focus:border-purple-500/60",
                      },
                    ].map((f) => (
                      <div key={f.name}>
                        <p className="text-[10px] text-stone-400 uppercase mb-1">
                          {f.label}
                        </p>
                        <input
                          type="number"
                          name={f.name}
                          value={form[f.name]}
                          onChange={handleChange}
                          disabled={isCreditWithBalance}
                          min="0"
                          step="0.01"
                          className={`w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-xs text-white outline-none ${f.color} font-mono transition-colors ${
                            isCreditWithBalance
                              ? "opacity-50 cursor-not-allowed bg-white/5"
                              : ""
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  {form.payment_mode === "split" &&
                    (() => {
                      const splitSum =
                        (parseFloat(form.credit_cash) || 0) +
                        (parseFloat(form.credit_card) || 0) +
                        (parseFloat(form.credit_upi) || 0);
                      const diff =
                        Math.round((newTotal - splitSum) * 100) / 100;
                      const matches = Math.abs(diff) <= 0.01;
                      return (
                        <div
                          className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs border ${matches ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}
                        >
                          <span className="font-medium">
                            Split Allocated:{" "}
                            <b className="font-mono text-white">
                              ₹{splitSum.toFixed(2)}
                            </b>{" "}
                            / ₹{newTotal.toFixed(2)}
                          </span>
                          <span className="font-bold">
                            {matches
                              ? "✓ Matches Total"
                              : diff > 0
                                ? `Remaining: ₹${diff.toFixed(2)}`
                                : `Excess: ₹${Math.abs(diff).toFixed(2)}`}
                          </span>
                        </div>
                      );
                    })()}

                  {form.payment_mode === "credit" &&
                    (() => {
                      const upfront =
                        (parseFloat(form.credit_cash) || 0) +
                        (parseFloat(form.credit_card) || 0) +
                        (parseFloat(form.credit_upi) || 0);
                      return (
                        <div className="flex items-center justify-between bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2">
                          <div className="flex flex-col">
                            <span className="text-[11px] text-rose-400 font-semibold">
                              Pending Balance Due (Credit Ledger)
                            </span>
                            <span className="text-[10px] text-stone-400">
                              Upfront Paid: ₹{upfront.toFixed(2)} of ₹
                              {newTotal.toFixed(2)}
                            </span>
                          </div>
                          <span className="font-mono font-black text-rose-400 text-sm">
                            ₹{derivedCreditAmount.toFixed(2)}
                          </span>
                        </div>
                      );
                    })()}
                </div>
              )}
              <div>
                <p className="text-[10px] text-blue-400 uppercase tracking-widest mb-1.5">
                  Transport Charges (₹)
                  <span className="ml-1 text-stone-600 normal-case">
                    — added to total
                  </span>
                </p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                    ₹
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.50"
                    value={transportCharges}
                    onChange={(e) =>
                      setTransportCharges(
                        Math.max(0, parseFloat(e.target.value) || 0),
                      )
                    }
                    className="w-full bg-blue-500/10 border border-blue-500/20 rounded-xl pl-6 pr-3 py-2 text-xs text-blue-300 outline-none focus:border-blue-500/50 font-mono transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Narration / Note"
                  name="narration"
                  value={form.narration}
                  onChange={handleChange}
                />
                <Field
                  label="Delivery Status"
                  name="_delivery"
                  value={deliveryStatus || ""}
                  onChange={(e) => setDeliveryStatus(e.target.value || null)}
                  options={[
                    { value: "", label: "Standard" },
                    { value: "pending_delivery", label: "📦 Pending Delivery" },
                    {
                      value: "partial",
                      label: "💰 Balance Due",
                    },
                    { value: "delivered", label: "✅ Delivered" },
                  ]}
                />
              </div>
              <div>
                <p className="text-[10px] text-amber-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Star size={10} /> Google Review
                </p>
                <textarea
                  name="google_review"
                  value={form.google_review || ""}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Customer's Google review or link"
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-amber-500/60 placeholder-stone-600 resize-none transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                    Referred By Staff
                  </p>
                  <div className="relative">
                    <select
                      name="referred_by_staff_id"
                      value={form.referred_by_staff_id || ""}
                      onChange={handleChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-amber-500/60 appearance-none pr-8 transition-colors"
                    >
                      <option value="" className="bg-[#1c1c2e] text-stone-400">
                        — None —
                      </option>
                      {staffList.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                          className="bg-[#1c1c2e] text-white"
                        >
                          {s.name} ({s.role})
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={11}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
                    />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-stone-500 uppercase tracking-widest mb-1">
                  Scheduled Delivery Date
                </p>
                <input
                  type="date"
                  name="scheduled_delivery_date"
                  value={form.scheduled_delivery_date || ""}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-amber-500/60 transition-colors"
                />
              </div>
            </div>
          )}

          {/* Items Section */}
          {orderCreditNotes.length > 0 && (
            <div className="mx-5 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">
                Returns / Credit Notes
              </p>
              {orderCreditNotes.map((note) => (
                <div key={note.id} className="text-xs text-stone-200 mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <b>{note.credit_note_number}</b>
                    <div className="flex gap-1">
                      <button
                        onClick={() => printCreditNote(note)}
                        className="rounded-lg bg-white/10 px-2 py-1 text-[10px]"
                      >
                        Print / PDF
                      </button>
                      <button
                        onClick={() => shareCreditNote(note)}
                        className="rounded-lg bg-white/10 px-2 py-1 text-[10px]"
                      >
                        Share
                      </button>
                    </div>
                  </div>
                  <div className="mt-1">
                    Issued {rupee(note.credit_issued)} · Used{" "}
                    {rupee(note.credit_used)} · Remaining{" "}
                    {rupee(note.remaining_credit)} · <b>{note.status}</b>
                  </div>
                  <div className="text-stone-400">
                    {(note.returned_items || [])
                      .map(
                        (item) =>
                          `${item.product_name} × ${item.quantity} @ ${rupee(item.price)}`,
                      )
                      .join(", ")}
                  </div>
                  {(note.usage_history || []).length > 0 && (
                    <div className="mt-1 text-stone-400">
                      Usage:{" "}
                      {(note.usage_history || [])
                        .map(
                          (u) =>
                            `${u.order_number || u.order_id || "—"} ${rupee(u.amount)} → ${rupee(u.remaining_credit)}`,
                        )
                        .join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="px-5 py-4 border-b border-white/8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                <Package size={10} />{" "}
                {mode === "edit" ? editItems.length : order.items?.length || 0}{" "}
                Items ·{" "}
                {mode === "edit"
                  ? editItems.reduce(
                      (sum, item) => sum + Number(item.quantity || 0),
                      0,
                    )
                  : totalItemQuantity}{" "}
                Pcs
              </p>
              {mode === "edit" && (
                <button
                  onClick={() => setShowAddProduct((prev) => !prev)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-black border transition-all ${showAddProduct ? "bg-amber-500/20 text-amber-400 border-amber-500/25" : "bg-white/10 hover:bg-white/15 text-stone-300 hover:text-white border-white/15"}`}
                >
                  {showAddProduct ? <X size={11} /> : <Plus size={11} />}
                  {showAddProduct ? "Close Search" : "Add Product"}
                </button>
              )}
            </div>

            {mode === "edit" ? (
              <div className="space-y-2">
                <AnimatePresence>
                  {editItems.map((item, i) => (
                    <motion.div
                      key={`${item.product_id}-${i}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10, height: 0 }}
                      className="flex flex-col gap-1.5 bg-white/5 border border-white/8 rounded-xl px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold truncate">
                            {item.product_name}
                          </p>
                          <p className="text-stone-400 text-[10px]">
                            ₹{item.price.toFixed(2)} each
                          </p>
                        </div>
                        <div className="flex items-center bg-white/10 border border-white/15 rounded-lg overflow-hidden shrink-0">
                          <button
                            onClick={() => updateItemQty(i, item.quantity - 1)}
                            className="px-2 py-1 text-stone-400 hover:text-white hover:bg-white/10 transition-colors text-xs"
                          >
                            <Minus size={10} />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItemQty(i, e.target.value)}
                            className="w-10 bg-transparent text-xs text-white text-center outline-none py-1"
                          />
                          <button
                            onClick={() => updateItemQty(i, item.quantity + 1)}
                            className="px-2 py-1 text-stone-400 hover:text-white hover:bg-white/10 transition-colors text-xs"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                        <p className="font-mono font-black text-xs text-amber-400 w-20 text-right shrink-0">
                          ₹
                          {(
                            item.price * item.quantity -
                            (item.discount || 0)
                          ).toFixed(2)}
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setEditItems((prev) =>
                              prev.map((entry, index) =>
                                index === i
                                  ? {
                                      ...entry,
                                      purchased: entry.purchased !== true,
                                    }
                                  : entry,
                              ),
                            )
                          }
                          className={`px-2 py-1 rounded-md text-[10px] font-black border ${item.purchased ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-white/10 text-stone-400 border-white/15"}`}
                          title="P means Purchased, not Paid"
                        >
                          {item.purchased ? "P ✓" : "P"}
                        </button>
                        <button
                          onClick={() => removeItem(i)}
                          className="p-1.5 text-stone-600 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all shrink-0"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>

                      <div className="flex items-center gap-2 pl-0.5">
                        <Tag size={10} className="text-stone-500 shrink-0" />
                        <p className="text-[10px] text-stone-500 shrink-0">
                          Item discount (₹):
                        </p>
                        <div className="relative w-28">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 text-[10px]">
                            ₹
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            max={item.price * item.quantity}
                            value={item.discount || 0}
                            onChange={(e) =>
                              updateItemDiscount(i, e.target.value)
                            }
                            className="w-full bg-amber-500/10 border border-amber-500/20 rounded-lg pl-5 pr-2 py-1 text-[11px] text-amber-300 outline-none focus:border-amber-500/50 font-mono transition-colors"
                          />
                        </div>
                        {(item.discount || 0) > 0 && (
                          <span className="text-[10px] text-amber-400 font-semibold">
                            −₹{(item.discount || 0).toFixed(2)} off
                          </span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {editItems.length === 0 && (
                  <div className="text-center py-6 text-stone-600 text-xs border border-dashed border-white/10 rounded-xl">
                    No items — add products using the button above
                  </div>
                )}
                <AnimatePresence>
                  {showAddProduct && (
                    <ProductSearchPanel
                      token={token}
                      existingProductIds={editItems.map((i) => i.product_id)}
                      onAdd={addProduct}
                      onClose={() => setShowAddProduct(false)}
                    />
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="space-y-2">
                {(order.items || []).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0 overflow-hidden">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package size={11} className="text-amber-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold flex items-center gap-1.5">
                          {item.purchased === true && (
                            <span className="text-[9px] font-black text-emerald-400 border border-emerald-500/40 rounded px-1">
                              P
                            </span>
                          )}
                          {item.product_name}
                        </p>
                        <p className="text-stone-400 text-[10px]">
                          {rupee(item.price)} × {item.quantity} Pcs
                          {item.gst_rate ? (
                            <span className="ml-1 text-emerald-500">
                              GST {item.gst_rate}%
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    <p className="font-mono font-black text-sm text-white">
                      {rupee(item.total)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Edit: Adjustments */}
          {mode === "edit" && (
            <div className="px-5 py-4 border-b border-white/8 space-y-3">
              <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold flex items-center gap-1.5">
                <Tag size={10} /> Post-Billing Adjustments
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-amber-400 uppercase tracking-widest mb-1.5">
                    Extra Discount (₹)
                    <span className="ml-1 text-stone-600 normal-case">
                      — added to original
                    </span>
                  </p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs">
                      ₹
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      min="0"
                      step="0.5"
                      value={extraDiscount}
                      onChange={(e) =>
                        setExtraDiscount(
                          Math.max(0, parseFloat(e.target.value) || 0),
                        )
                      }
                      className="w-full bg-amber-500/10 border border-amber-500/20 rounded-xl pl-6 pr-3 py-2 text-xs text-amber-300 outline-none focus:border-amber-500/50 font-mono transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-emerald-400 uppercase tracking-widest mb-1.5">
                    Original Discount (₹)
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={originalDiscount}
                    onChange={(e) =>
                      setOriginalDiscount(
                        Math.max(0, parseFloat(e.target.value) || 0),
                      )
                    }
                    className="w-full bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2 text-xs text-emerald-300 outline-none focus:border-emerald-500/50 font-mono"
                  />
                </div>
              </div>
              <div className="bg-white/5 border border-white/8 rounded-xl p-3 space-y-1.5">
                <p className="text-[9px] text-stone-600 uppercase tracking-widest mb-2">
                  Live Total Preview
                </p>
                <div className="flex justify-between text-xs">
                  <span className="text-stone-400">Items Subtotal</span>
                  <span className="font-mono text-white">
                    ₹{newSubtotal.toFixed(2)}
                  </span>
                </div>
                {(parseFloat(transportCharges) || 0) > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-blue-400 font-semibold flex items-center gap-1">
                      <Truck size={10} /> Transport Charges
                    </span>
                    <span className="font-mono text-blue-400">
                      +₹{(parseFloat(transportCharges) || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {originalDiscount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-400">Original Discount</span>
                    <span className="font-mono text-emerald-400">
                      −₹{originalDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                {extraDiscount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-amber-400 font-semibold">
                      Extra Discount
                    </span>
                    <span className="font-mono text-amber-400">
                      −₹{extraDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                {(order.tax || 0) > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-400">Tax (GST)</span>
                    <span className="font-mono text-stone-300">
                      +₹{(order.tax || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {manualRoundOff !== 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-blue-400 font-semibold">
                      Round Off
                    </span>
                    <span
                      className={`font-mono ${manualRoundOff > 0 ? "text-blue-400" : "text-emerald-400"}`}
                    >
                      {manualRoundOff > 0 ? "+" : ""}₹
                      {manualRoundOff.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-black border-t border-white/10 pt-2 mt-1">
                  <span className="text-white">New Total</span>
                  <span className="font-mono text-amber-400">
                    ₹{newTotal.toFixed(2)}
                  </span>
                </div>
                {newTotal !== (order.total || 0) && (
                  <p className="text-[10px] text-stone-500 text-right">
                    was ₹{(order.total || 0).toFixed(2)} →
                    <span
                      className={`ml-1 font-semibold ${newTotal < order.total ? "text-emerald-400" : "text-rose-400"}`}
                    >
                      {newTotal < order.total ? "▼" : "▲"} ₹
                      {Math.abs(newTotal - order.total).toFixed(2)}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Totals (view mode) */}
          {mode === "view" && (
            <div className="px-5 py-4 space-y-2">
              {[
                {
                  label: "Subtotal",
                  value: rupee(order.subtotal),
                  color: "text-stone-300",
                },
                order.discount > 0 && {
                  label: "Discount",
                  value: `−${rupee(order.discount)}`,
                  color: "text-emerald-400",
                },
                (order.extra_discount || 0) > 0 && {
                  label: "Extra Discount",
                  value: `−${rupee(order.extra_discount)}`,
                  color: "text-amber-400",
                },
                (order.transport_charges || 0) > 0 && {
                  label: "Transport Charges",
                  value: `+${rupee(order.transport_charges)}`,
                  color: "text-blue-400",
                  icon: <Truck size={10} />,
                },
                hasGST && {
                  label: "Tax (GST)",
                  value: rupee(order.tax),
                  color: "text-stone-300",
                },
                order.round_off &&
                  order.round_off !== 0 && {
                    label: "Round Off",
                    value: `${order.round_off > 0 ? "+" : ""}${rupee(order.round_off)}`,
                    color: "text-blue-400",
                  },
              ]
                .filter(Boolean)
                .map((row, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-stone-400">{row.label}</span>
                    <span className={`font-mono font-semibold ${row.color}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <span className="font-black text-white text-base">Total</span>
                <span className="font-mono font-black text-xl text-amber-400">
                  {rupee(order.total)}
                </span>
              </div>

              {(order.payment_mode === "credit" ||
                order.payment_mode === "split" ||
                order.credit_cash > 0 ||
                order.credit_card > 0 ||
                order.credit_upi > 0) && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                  <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                    Payment Breakdown
                  </p>
                  {[
                    {
                      icon: "💵",
                      label: "Cash received",
                      val: order.credit_cash,
                      color: "text-emerald-400",
                    },
                    {
                      icon: "💳",
                      label: "Card received",
                      val: order.credit_card,
                      color: "text-emerald-400",
                    },
                    {
                      icon: "📲",
                      label: "UPI received",
                      val: order.credit_upi,
                      color: "text-emerald-400",
                    },
                  ].map((r) => (
                    <div
                      key={r.label}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-stone-400">
                        {r.icon} {r.label}
                      </span>
                      <span className={`font-mono ${r.color}`}>
                        {rupee(r.val)}
                      </span>
                    </div>
                  ))}
                  {/* FIX: Use computedAmountReceived instead of order.amount_received */}
                  <div className="flex items-center justify-between text-xs font-bold border-t border-white/8 pt-1.5">
                    <span className="text-stone-300">Total received</span>
                    <span className="font-mono text-emerald-400">
                      {rupee(computedAmountReceived)}
                    </span>
                  </div>
                  {computedBalanceDue > 0 && (
                    <div className="flex items-center justify-between text-xs font-bold">
                      <span className="text-rose-300">Balance due</span>
                      <span className="font-mono text-rose-400">
                        {rupee(computedBalanceDue)}
                      </span>
                    </div>
                  )}
                  {/* Payment collection history - shown in view mode */}
                  {(order.payment_collections || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
                        <Clock size={10} /> Collection History
                      </p>
                      <div className="space-y-1.5">
                        {(order.payment_collections || []).map((c, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2"
                          >
                            <div>
                              <p className="text-xs font-semibold text-white capitalize">
                                Collection {i + 1} · {c.mode}
                              </p>
                              <p className="text-[10px] text-stone-500">
                                {fmtFull(c.collected_at)}
                                {c.collected_by && (
                                  <span className="ml-1 text-stone-600">
                                    · {c.collected_by}
                                  </span>
                                )}
                              </p>
                            </div>
                            <p className="font-mono font-black text-emerald-400 text-sm">
                              +₹{(c.amount || 0).toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {order.narration && (
                <p className="text-xs text-stone-500 italic bg-white/5 rounded-xl px-3 py-2 mt-2">
                  "{order.narration}"
                </p>
              )}
              {order.google_review && (
                <div className="mt-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">
                  <p className="text-[10px] text-amber-400 uppercase tracking-widest font-bold mb-1 flex items-center gap-1">
                    <Star size={10} /> Google Review
                  </p>
                  <p className="text-xs text-stone-300 whitespace-pre-line">
                    {order.google_review}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Print footer */}
          <div className="px-5 pb-5 pt-1 flex gap-2 border-t border-white/8">
            <button
              onClick={() =>
                printInvoice(
                  enrichedForPrint({
                    ...displayOrder,
                    invoice_type: "non_gst",
                    gst_type: null,
                  }),
                  currentUserName,
                )
              }
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-stone-700/50 hover:bg-stone-700 border border-white/10 text-stone-300 hover:text-white text-[10px] font-bold transition-all"
            >
              <Printer size={11} /> Non-GST Receipt
            </button>
            <button
              onClick={() =>
                printInvoice(
                  enrichedForPrint({
                    ...displayOrder,
                    invoice_type: "gst",
                    gst_type: order.gst_type || "cgst_sgst",
                  }),
                  currentUserName,
                )
              }
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 hover:text-emerald-300 text-[10px] font-bold transition-all"
            >
              <Printer size={11} /> GST Invoice
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showEWay && (
            <EWayBillModal
              order={displayOrder}
              onClose={() => setShowEWay(false)}
            />
          )}
          {showSchedule && (
            <ScheduleDeliveryModal
              order={order}
              token={token}
              onClose={() => setShowSchedule(false)}
              onScheduled={(updated) => {
                onUpdated(updated);
                setDeliveryStatus(updated.status);
              }}
            />
          )}
          {showCollectBalance && (
            <CollectBalanceModal
              order={order}
              token={token}
              currentUserName={currentUserName}
              onClose={() => setShowCollectBalance(false)}
              onCollected={(updated) => {
                onUpdated(updated);
                setDeliveryStatus(updated.status);
              }}
            />
          )}
          {showReturnItems && (
            <ReturnItemsModal
              order={order}
              token={token}
              existingNotes={orderCreditNotes}
              onClose={() => setShowReturnItems(false)}
              onUpdated={() => onUpdated?.(order)}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// ─── Order Row ─────────────────────────────────────────────────
const ORDER_COLUMN_WIDTHS = ["28%", "12%", "11%", "11%", "14%", "12%", "12%"];

const OrderTableColumns = () => (
  <colgroup>
    {ORDER_COLUMN_WIDTHS.map((width, index) => (
      <col key={index} style={{ width }} />
    ))}
  </colgroup>
);

const OrderRow = ({ order, index, onView }) => (
  <motion.tr
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.025 }}
    className="group border-b border-stone-100 hover:bg-amber-50/40 transition-colors cursor-pointer"
    onClick={() => onView()}
  >
    <td className="px-5 py-3.5 align-top overflow-hidden">
      <p className="font-mono font-bold text-xs text-stone-800 whitespace-nowrap">
        {order.invoice_number}
      </p>
      {order.customer_name && (
        <p className="text-[11px] leading-tight text-stone-400 mt-1 flex items-start gap-1 break-words">
          <span className="shrink-0">Customer:</span>{" "}
          <User size={9} className="shrink-0 mt-0.5" />{" "}
          <span>{order.customer_name}</span>
        </p>
      )}
      {order.staff_name && (
        <p className="text-[11px] leading-tight text-stone-400 mt-1 flex items-start gap-1 break-words">
          <span className="shrink-0">Staff:</span>{" "}
          <User size={9} className="shrink-0 mt-0.5" />{" "}
          <span>{order.staff_name}</span>
        </p>
      )}
    </td>
    <td className="px-5 py-3.5">
      <p className="text-xs font-semibold text-stone-700">
        {fmt(order.created_at)}
      </p>
      <p className="text-[10px] text-stone-400">{fmtTime(order.created_at)}</p>
    </td>
    <td className="px-5 py-3.5">
      <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-600 text-[10px] font-bold px-2.5 py-1 rounded-full">
        <Package size={9} /> {order.items?.length || 0} items
      </span>
    </td>
    <td className="px-5 py-3.5">
      <span
        className={`text-[10px] font-black px-2 py-1 rounded-full border capitalize ${pmColor(getPaymentLabel(order))}`}
      >
        {getPaymentLabel(order)}
      </span>
    </td>
    <td className="px-5 py-3.5">
      <DeliveryBadge
        status={order.status}
        scheduledDate={order.scheduled_delivery_date}
        balanceDue={getReceivedAndBalance(order).balance}
        small
      />
      {order.scheduled_delivery_date &&
        (order.status === "pending_delivery" || order.status === "partial") && (
          <p
            className={`text-[9px] mt-0.5 font-semibold ${isPast(order.scheduled_delivery_date) ? "text-rose-400" : isToday(order.scheduled_delivery_date) ? "text-orange-400" : "text-stone-400"}`}
          >
            {fmtDate(order.scheduled_delivery_date)}
          </p>
        )}
    </td>
    <td className="px-5 py-3.5">
      <p className="font-mono font-black text-sm text-stone-800">
        {rupee(order.total)}
      </p>
      {order.status === "partial" &&
        (() => {
          const { received: tblReceived, balance: tblBalance } =
            getReceivedAndBalance(order);
          return tblBalance > 0 ? (
            <div>
              <p className="text-[12px] text-emerald-400 font-semibold">
                ₹{tblReceived.toFixed(2)} received
              </p>
              <p className="text-[12px] text-rose-500 font-semibold">
                ₹{tblBalance.toFixed(2)} due
              </p>
            </div>
          ) : null;
        })()}
      {(order.extra_discount || 0) > 0 && (
        <p className="text-[12px] text-amber-600 font-semibold">
          −₹{order.extra_discount?.toFixed(2)} disc.
        </p>
      )}
    </td>
    <td className="px-5 py-3.5">
      {order.status === "delivered" ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
          <CheckCircle2 size={9} /> Delivered
        </span>
      ) : order.status === "partial" ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-pink-500/15 text-pink-400 border-pink-500/20">
          <IndianRupee size={9} /> Balance Due
        </span>
      ) : order.status === "pending_delivery" ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/20">
          <Clock size={9} /> Pending Pickup
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-stone-500/15 text-stone-400 border-stone-500/20">
          <CheckCircle2 size={9} /> Completed
        </span>
      )}
    </td>
  </motion.tr>
);

// ─── Mobile Card ───────────────────────────────────────────────
const OrderCard = ({ order, index, onView }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04 }}
    onClick={onView}
    className="bg-white border border-stone-100 rounded-2xl p-4 hover:shadow-md transition-all cursor-pointer active:scale-[0.98]"
  >
    <div className="flex items-start justify-between mb-2.5">
      <div>
        <p className="font-mono font-bold text-xs text-stone-800">
          {order.invoice_number}
        </p>
        <p className="text-[10px] text-stone-400 mt-0.5">
          {fmt(order.created_at)} · {fmtTime(order.created_at)}
        </p>
      </div>
      <div className="text-right">
        <p className="font-mono font-black text-base text-stone-800">
          {rupee(order.total)}
        </p>
        {order.status === "partial" && (order.balance_due || 0) > 0 && (
          <p className="text-[9px] text-rose-500 font-semibold">
            ₹{order.balance_due?.toFixed(2)} due
          </p>
        )}
        {(order.extra_discount || 0) > 0 && (
          <p className="text-[9px] text-amber-600 font-semibold">
            −₹{order.extra_discount?.toFixed(2)} extra disc.
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
        <Package size={8} /> {order.items?.length || 0} items
      </span>
      <span
        className={`text-[10px] font-black px-2 py-0.5 rounded-full border capitalize ${pmColor(getPaymentLabel(order))}`}
      >
        {getPaymentLabel(order)}
      </span>
      {order.status && (
        <DeliveryBadge
          status={order.status}
          scheduledDate={order.scheduled_delivery_date}
          balanceDue={getReceivedAndBalance(order).balance}
          small
        />
      )}
      {order.scheduled_delivery_date &&
        (order.status === "pending_delivery" || order.status === "partial") && (
          <span
            className={`text-[9px] font-semibold ${isPast(order.scheduled_delivery_date) ? "text-rose-400" : isToday(order.scheduled_delivery_date) ? "text-orange-400" : "text-stone-400"}`}
          >
            {fmtDate(order.scheduled_delivery_date)}
          </span>
        )}
      {order.customer_name && (
        <span className="text-[10px] text-stone-400 truncate ml-auto">
          {order.customer_name}
        </span>
      )}
    </div>
  </motion.div>
);

// ─── Main Orders Page ──────────────────────────────────────────
const Orders = () => {
  const searchDebounceRef = useRef(null);

  const { token, user } = useAuth();
  const { t } = useTranslation();
  const canViewOrders = ["admin", "billing"].includes(user?.role);
  const [orders, setOrders] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [exportMonth, setExportMonth] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterPayment, setFilterPayment] = useState("all");
  const [filterDelivery, setFilterDelivery] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [datePreset, setDatePreset] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all"); // all/pending/partial/delivered
  const [customDateFrom, setCustomDateFrom] = useState(""); // replaces customDate
  const [customDateTo, setCustomDateTo] = useState("");
  const [dateRange, setDateRange] = useState(undefined);
  const [filterStaff, setFilterStaff] = useState("");
  const ordersUnlockKey = `orders-page-unlocked:${user?.id || "guest"}`;
  const [ordersUnlocked, setOrdersUnlocked] = useState(false);
  const [ordersPasswordConfigured, setOrdersPasswordConfigured] =
    useState(null);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockPasswordConfirm, setUnlockPasswordConfirm] = useState("");
  const [unlockingOrders, setUnlockingOrders] = useState(false);

  useEffect(() => {
    setOrdersUnlocked(sessionStorage.getItem(ordersUnlockKey) === "1");
    if (!token) return;
    axios
      .get(`${API_URL}/auth/orders-lock-password/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setOrdersPasswordConfigured(Boolean(data.configured)))
      .catch(() => setOrdersPasswordConfigured(false));
  }, [ordersUnlockKey, token]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const LIMIT = 50;
  const DATE_PRESETS = [
    { key: "all", label: "All Time", from: null, to: null },
    { key: "today", label: "Today", from: todayStr(), to: todayStr() },
    {
      key: "yesterday",
      label: "Yesterday",
      from: yesterdayStr(),
      to: yesterdayStr(),
    },
    { key: "last7", label: "Last 7 Days", from: daysAgoStr(6), to: todayStr() },
    {
      key: "thisWeek",
      label: "This Week",
      from: startOfWeekStr(),
      to: todayStr(),
    },
    {
      key: "lastWeek",
      label: "Last Week",
      from: startOfLastWeekStr(),
      to: endOfLastWeekStr(),
    },
    {
      key: "thisMonth",
      label: "This Month",
      from: startOfMonthStr(),
      to: todayStr(),
    },
    {
      key: "lastMonth",
      label: "Last Month",
      from: startOfLastMonthStr(),
      to: endOfLastMonthStr(),
    },
    { key: "custom", label: "Custom Range", from: null, to: null },
  ];
  const { dateFrom, dateTo } = useMemo(() => {
    if (datePreset === "custom")
      return { dateFrom: customDateFrom, dateTo: customDateTo };
    const preset = DATE_PRESETS.find((p) => p.key === datePreset);
    return { dateFrom: preset?.from || "", dateTo: preset?.to || "" };
  }, [datePreset, customDateFrom, customDateTo]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 1000);
    return () => clearTimeout(t);
  }, [search]);

  const statusParam = useMemo(() => {
    if (statusFilter === "pending") return "pending";
    if (statusFilter === "partial") return "partial";
    if (statusFilter === "delivered") return "delivered";
    return "";
  }, [statusFilter]);

  const fetchOrders = useCallback(
    async (pg = 1) => {
      if (!ordersUnlocked) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: pg,
          limit: LIMIT,
          search: debouncedSearch,
          payment: filterPayment === "all" ? "" : filterPayment.toLowerCase(),
          status: statusParam,
          date_from: dateFrom,
          date_to: dateTo,
          referred_by_staff_id: filterStaff,
        });

        const [ordersRes, usersRes] = await Promise.all([
          axios.get(`${API_URL}/orders?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          staffs.length === 0
            ? axios.get(`${API_URL}/users`, {
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve({ data: staffs }),
        ]);

        const users = Array.isArray(usersRes.data) ? usersRes.data : staffs;
        const userMap = Object.fromEntries(users.map((u) => [u.id, u.name]));
        if (staffs.length === 0) setStaffs(users);

        const enriched = (ordersRes.data.orders || []).map((o) => ({
          ...o,
          staff_name: userMap[o.referred_by_staff_id] || null,
        }));

        setOrders(enriched);
        setPagination(ordersRes.data.pagination);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      debouncedSearch,
      filterPayment,
      statusParam,
      dateFrom,
      dateTo,
      filterStaff,
      ordersUnlocked,
    ],
  );

  useEffect(() => {
    setPagination((current) => ({ ...current, page: 1 }));
    fetchOrders(1);
  }, [
    debouncedSearch,
    filterPayment,
    datePreset,
    statusFilter,
    customDateFrom,
    customDateTo,
    filterStaff,
    ordersUnlocked,
  ]);

  const unlockOrders = async (event) => {
    event.preventDefault();
    if (!unlockPassword) return;
    if (
      ordersPasswordConfigured === false &&
      unlockPassword !== unlockPasswordConfirm
    ) {
      toast.error("Passwords do not match");
      return;
    }
    setUnlockingOrders(true);
    try {
      const endpoint = ordersPasswordConfigured
        ? `${API_URL}/auth/orders-lock-password/verify`
        : `${API_URL}/auth/orders-lock-password`;
      await axios({
        method: ordersPasswordConfigured ? "post" : "put",
        url: endpoint,
        data: { password: unlockPassword },
        headers: { Authorization: `Bearer ${token}` },
      });
      sessionStorage.setItem(ordersUnlockKey, "1");
      setOrdersUnlocked(true);
      setUnlockPassword("");
      setUnlockPasswordConfirm("");
      setOrdersPasswordConfigured(true);
      toast.success("Orders page unlocked");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Incorrect password");
    } finally {
      setUnlockingOrders(false);
    }
  };

  const lockOrders = () => {
    sessionStorage.removeItem(ordersUnlockKey);
    setOrdersUnlocked(false);
    setOrders([]);
    setSelectedOrder(null);
    toast.success("Orders page locked");
  };

  useEffect(() => {
    if (selectedOrder) {
      const fresh = orders.find((o) => o.id === selectedOrder.id);
      if (fresh) setSelectedOrder(fresh);
    }
  }, [orders]);

  // const filtered = useMemo(() => {
  //   return orders.filter((order) => {
  //     const date = order.created_at.split("T")[0];
  //     const today = new Date().toISOString().split("T")[0];
  //     const y = new Date();
  //     y.setDate(y.getDate() - 1);
  //     const yesterday = y.toISOString().split("T")[0];

  //     if (filter === "today" && date !== today) return false;
  //     if (filter === "yesterday" && date !== yesterday) return false;
  //     if (filter === "custom" && date !== customDate) return false;
  //     if (filter === "pending" && order.status !== "pending_delivery")
  //       return false;
  //     if (filter === "partial" && order.status !== "partial") return false;
  //     if (filter === "delivered" && order.status !== "delivered") return false;

  //     if (filterPayment !== "all") {
  //       const mode = (order.payment_mode || "").toLowerCase();
  //       if (filterPayment === "Cash") {
  //         if (
  //           mode !== "Cash" &&
  //           !(mode === "Credit" && (order.credit_cash || 0) > 0)
  //         )
  //           return false;
  //       } else if (filterPayment === "Card") {
  //         if (
  //           mode !== "Card" &&
  //           !(mode === "Credit" && (order.credit_card || 0) > 0)
  //         )
  //           return false;
  //       } else if (filterPayment === "UPI") {
  //         if (
  //           mode !== "UPI" &&
  //           !(mode === "Credit" && (order.credit_upi || 0) > 0)
  //         )
  //           return false;
  //       } else {
  //         if (mode !== filterPayment) return false;
  //       }
  //     }

  //     if (search) {
  //       const q = search.toLowerCase();
  //       if (
  //         !order.invoice_number?.toLowerCase().includes(q) &&
  //         !order.customer_name?.toLowerCase().includes(q)
  //       )
  //         return false;
  //     }

  //     return true;
  //   });
  // }, [orders, filter, customDate, filterPayment, search]);
  const filtered = orders;
  const currentPage = pagination.page || 1;
  const totalPages =
    pagination.pages || Math.max(1, Math.ceil((pagination.total || 0) / LIMIT));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage || loading)
      return;
    setPagination((current) => ({ ...current, page }));
    fetchOrders(page);
  };

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      options.push({ value, label });
    }
    return options;
  }, []);

  const stats = useMemo(
    () => ({
      count: pagination.total,
      revenue: filtered.reduce((s, o) => s + (o.total || 0), 0),
      today: filtered.filter(
        (o) => fmt(o.created_at) === fmt(new Date().toISOString()),
      ).length,
      pendingPickup: filtered.filter((o) => o.status === "pending_delivery")
        .length,
      pendingPartial: filtered.filter((o) => o.status === "partial").length,
      todayDeliveries: filtered.filter(
        (o) =>
          isToday(o.scheduled_delivery_date) &&
          (o.status === "pending_delivery" || o.status === "partial"),
      ).length,
      overdueDeliveries: filtered.filter(
        (o) =>
          isPast(o.scheduled_delivery_date) &&
          (o.status === "pending_delivery" || o.status === "partial"),
      ).length,
      totalBalanceDue: filtered
        .filter((o) => o.status === "partial")
        .reduce((s, o) => {
          const received = Math.max(
            parseFloat(o.amount_received) || 0,
            parseFloat(o.amount_paid) || 0,
            (parseFloat(o.credit_cash) || 0) +
              (parseFloat(o.credit_card) || 0) +
              (parseFloat(o.credit_upi) || 0),
          );
          return s + Math.max(0, (parseFloat(o.total) || 0) - received);
        }, 0),
    }),
    [filtered],
  );

  // const handleUpdated = (updatedOrder) => {
  //   setOrders((prev) =>
  //     prev.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)),
  //   );
  //   setSelectedOrder(updatedOrder);
  // };
  // const handleDeleted = (deletedId) => {
  //   setOrders((prev) => prev.filter((o) => o.id !== deletedId));
  // };

  const handleUpdated = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setSelectedOrder(updated);
  };
  const handleDeleted = (id) => {
    setOrders((prev) => prev.filter((o) => o.id !== id));
    setPagination((p) => ({ ...p, total: p.total - 1 }));
  };

  if (!canViewOrders)
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] bg-[#f7f6f3]">
        <div className="p-5 bg-stone-100 rounded-2xl mb-4">
          <FileText size={44} className="text-stone-300" />
        </div>
        <p className="font-black text-stone-600 text-lg">Access Denied</p>
        <p className="text-stone-400 text-sm mt-1">
          Only Admin and Billing staff can view orders.
        </p>
      </div>
    );

  if (loading && ordersUnlocked)
    return (
      <div className="flex items-center justify-center h-full bg-[#f7f6f3]">
        <div className="text-center">
          <div className="relative w-14 h-14 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-amber-100" />
            <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-stone-400 text-sm">Loading orders…</p>
        </div>
      </div>
    );

  // Add export function
  const handleExportToTally = async () => {
    if (!exportMonth) {
      toast.error("Please select a month to export");
      return;
    }
    setExporting(true);
    try {
      const [year, month] = exportMonth.split("-");
      const fromDate = `${exportMonth}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const toDate = `${exportMonth}-${String(lastDay).padStart(2, "0")}`;

      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
      });
      const response = await axios.post(
        `${API_URL}/orders/export/tally-excel?${params.toString()}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const monthLabel = new Date(exportMonth + "-01").toLocaleDateString(
        "en-IN",
        {
          month: "long",
          year: "numeric",
        },
      );
      link.setAttribute(
        "download",
        `Tally_${monthLabel.replace(" ", "_")}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${monthLabel} to Excel`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  if (!canViewOrders) {
    return (
      <div className="p-8 text-stone-500">
        You do not have access to orders.
      </div>
    );
  }

  if (!ordersUnlocked) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <form
          onSubmit={unlockOrders}
          className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-8 shadow-sm"
        >
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <AlertCircle size={28} />
          </div>
          <h1 className="text-center text-2xl font-bold text-stone-900">
            Orders Locked
          </h1>
          <p className="mt-2 text-center text-sm text-stone-500">
            {ordersPasswordConfigured === false
              ? "Create a separate password to protect this page."
              : "Enter your Orders password to view orders."}
          </p>
          <input
            type="password"
            value={unlockPassword}
            onChange={(event) => setUnlockPassword(event.target.value)}
            placeholder={
              ordersPasswordConfigured === false
                ? "Create Orders password"
                : "Enter Orders password"
            }
            autoFocus
            className="mt-6 w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:border-amber-500"
          />
          {ordersPasswordConfigured === false && (
            <input
              type="password"
              value={unlockPasswordConfirm}
              onChange={(event) => setUnlockPasswordConfirm(event.target.value)}
              placeholder="Confirm Orders password"
              className="mt-3 w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:border-amber-500"
            />
          )}
          <button
            type="submit"
            disabled={
              unlockingOrders ||
              !unlockPassword ||
              ordersPasswordConfigured === null
            }
            className="mt-4 w-full rounded-xl bg-amber-500 px-4 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {unlockingOrders
              ? ordersPasswordConfigured === false
                ? "Saving…"
                : "Verifying…"
              : ordersPasswordConfigured === false
                ? "Set Password & Unlock"
                : "Unlock Orders"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f7f6f3] p-4 md:p-6 lg:p-8">
      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-[#1c1c2e] rounded-2xl p-6 mb-6 shadow-xl"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 40%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 10% 70%, #10b981 0%, transparent 45%)",
          }}
        />
        <div className="relative flex items-center gap-4">
          <div className="p-3 bg-amber-500/15 rounded-2xl border border-amber-500/20">
            <FileText size={24} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              Order History
            </h1>
            <p className="text-stone-400 text-sm mt-0.5">
              All billing transactions — fully editable
            </p>
          </div>
          <button
            type="button"
            onClick={lockOrders}
            className="ml-auto inline-flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/20"
            title="Lock Orders page"
          >
            <LockKeyhole size={14} /> Lock Orders
          </button>
        </div>
        <div className="relative mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Total Orders",
              value: stats.count,
              color: "text-stone-300",
            },
            {
              label: "Total Revenue",
              value: `₹${stats.revenue.toFixed(0)}`,
              color: "text-amber-400",
            },
            {
              label: "Today's Orders",
              value: stats.today,
              color: "text-emerald-400",
            },
            {
              label: "Today's Deliveries",
              value: stats.todayDeliveries,
              color:
                stats.todayDeliveries > 0
                  ? "text-orange-400"
                  : "text-stone-500",
            },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                {s.label}
              </p>
              <p className={`font-mono font-black text-2xl ${s.color}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Alert banners */}
      {stats.overdueDeliveries > 0 && filterDate !== "overdue_delivery" && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 bg-rose-50 border border-rose-200 rounded-2xl px-5 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <AlertCircle size={16} className="text-rose-500" />
            <p className="text-sm font-semibold text-rose-800">
              {stats.overdueDeliveries} overdue delivery
              {stats.overdueDeliveries > 1 ? "ies" : ""} — customers haven't
              collected yet
            </p>
          </div>
          <button
            onClick={() => {
              setStatusFilter("pending");
              setDatePreset("all");
            }}
            className="text-xs font-black text-rose-700 hover:text-rose-900 underline underline-offset-2"
          >
            View →
          </button>
        </motion.div>
      )}
      {stats.todayDeliveries > 0 && filterDate !== "today_delivery" && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 bg-orange-50 border border-orange-200 rounded-2xl px-5 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-orange-500" />
            <p className="text-sm font-semibold text-orange-800">
              {stats.todayDeliveries} delivery
              {stats.todayDeliveries > 1 ? "ies" : ""} scheduled for today
            </p>
          </div>
          <button
            onClick={() => {
              setStatusFilter("pending");
              setDatePreset("today");
            }}
            className="text-xs font-black text-orange-700 hover:text-orange-900 underline underline-offset-2"
          >
            View →
          </button>
        </motion.div>
      )}
      {stats.pendingPartial > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 bg-pink-50 border border-pink-200 rounded-2xl px-5 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <IndianRupee size={16} className="text-pink-500" />
            <p className="text-sm font-semibold text-pink-800">
              {stats.pendingPartial} partial payment order
              {stats.pendingPartial > 1 ? "s" : ""} — ₹
              {stats.totalBalanceDue.toFixed(2)} balance to collect on delivery
            </p>
          </div>
        </motion.div>
      )}
      {stats.pendingPickup > 0 && filterDelivery !== "pending" && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-amber-500" />
            <p className="text-sm font-semibold text-amber-800">
              {stats.pendingPickup} order{stats.pendingPickup > 1 ? "s" : ""}{" "}
              paid but products not yet collected
            </p>
          </div>
        </motion.div>
      )}

      {/* Status Tab Bar */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3 mb-3 shadow-sm">
        <div className="flex gap-1 flex-wrap">
          {[
            { key: "all", label: "All Orders", icon: <FileText size={12} /> },
            {
              key: "pending",
              label: "Pending Pickup",
              icon: <Clock size={12} />,
            },
            {
              key: "partial",
              label: "Balance Due",
              icon: <IndianRupee size={12} />,
            },
            {
              key: "delivered",
              label: "Delivered",
              icon: <CheckCircle2 size={12} />,
            },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                statusFilter === tab.key
                  ? "bg-amber-500 text-black"
                  : "bg-stone-100 text-stone-500 hover:bg-stone-200"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 mb-5 shadow-sm space-y-2.5">
        {/* Row 1 — search + date preset + clear */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-36">
            <Search
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice or customer…"
              className="w-full h-9 pl-8 pr-3 text-xs bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/30 text-stone-700"
            />
          </div>

          <DateRangePicker
            date={dateRange}
            onDateChange={(range) => {
              setDateRange(range);
              setDatePreset(range?.from ? "custom" : "all");
              setCustomDateFrom(toDateInput(range?.from));
              setCustomDateTo(toDateInput(range?.to || range?.from));
            }}
            placeholder="Select date range"
            className="h-9 w-[240px] rounded-xl border-stone-200 bg-white text-xs text-stone-600"
          />

          <select
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
            className="h-9 px-3 rounded-xl border border-stone-200 bg-white text-xs text-stone-600 outline-none"
          >
            <option value="all">All Payments</option>
            <option value="Cash">Cash</option>
            <option value="Card">Card</option>
            <option value="UPI">UPI</option>
            <option value="Split">Split</option>
            <option value="Credit">Credit</option>
          </select>

          {/* Staff referral filter */}
          <select
            value={filterStaff}
            onChange={(e) => setFilterStaff(e.target.value)}
            className="h-9 px-3 rounded-xl border border-stone-200 bg-white text-xs text-stone-600 outline-none"
          >
            <option value="">All Staff</option>
            {staffs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.role})
              </option>
            ))}
          </select>

          {(search ||
            filterPayment !== "all" ||
            statusFilter !== "all" ||
            datePreset !== "today" ||
            filterStaff) && (
            <button
              onClick={() => {
                setSearch("");
                setFilterPayment("all");
                setStatusFilter("all");
                setDatePreset("today");
                setCustomDateFrom("");
                setCustomDateTo("");
                setDateRange(undefined);
                setFilterStaff("");
              }}
              className="h-9 px-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-xs font-bold flex items-center gap-1"
            >
              <X size={11} /> Clear
            </button>
          )}

          <span className="ml-auto text-xs text-stone-400 font-semibold">
            {filtered.length} orders
          </span>
        </div>

        {/* Active filter chips */}
        {(filterPayment !== "all" || filterStaff || dateRange?.from) && (
          <div className="flex flex-wrap gap-1.5">
            {filterPayment !== "all" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                {filterPayment}
                <button onClick={() => setFilterPayment("all")}>
                  <X size={9} />
                </button>
              </span>
            )}
            {filterStaff && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                <User size={9} />
                {staffs.find((s) => s.id === filterStaff)?.name || "Staff"}
                <button onClick={() => setFilterStaff("")}>
                  <X size={9} />
                </button>
              </span>
            )}
            {dateRange?.from && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <Calendar size={9} />
                {fmtDate(customDateFrom)} — {fmtDate(customDateTo)}
                <button
                  onClick={() => {
                    setDatePreset("all");
                    setCustomDateFrom("");
                    setCustomDateTo("");
                    setDateRange(undefined);
                  }}
                >
                  <X size={9} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Tally Export ── */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 mb-5 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 shrink-0">
          <div className="p-2 bg-blue-500/10 rounded-xl border border-blue-500/20">
            <FileText size={15} className="text-blue-600" />
          </div>
          <span className="text-sm font-bold text-stone-700">
            Export to Tally
          </span>
        </div>

        <select
          value={exportMonth}
          onChange={(e) => setExportMonth(e.target.value)}
          className="h-9 px-3 rounded-xl border border-stone-200 bg-white text-xs text-stone-700 outline-none focus:ring-2 focus:ring-blue-400 flex-1 min-w-40"
        >
          <option value="">— Select Month —</option>
          {monthOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <button
          onClick={handleExportToTally}
          disabled={exporting || !exportMonth}
          className="h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs flex items-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
        >
          {exporting ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <FileText size={13} />
          )}
          {exporting ? "Generating…" : "Download Excel"}
        </button>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-stone-100">
          <div className="p-4 bg-amber-50 rounded-2xl mb-3">
            <FileText size={28} className="text-amber-300" />
          </div>
          <p className="text-stone-500 font-semibold text-sm">
            No orders found
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {orders.length === 0
              ? "Orders will appear after billing"
              : "Try adjusting your filters"}
          </p>
        </div>
      ) : (
        <>
          <div className="md:hidden space-y-2.5">
            {filtered.map((o, i) => (
              <OrderCard
                key={o.id}
                order={o}
                index={i}
                onView={() => setSelectedOrder(o)}
              />
            ))}
            <div className="flex items-center justify-between border-t border-stone-100 pt-3 text-xs text-stone-500">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="rounded-lg border border-stone-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className="rounded-lg border border-stone-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
          <div className="hidden md:block bg-white rounded-2xl border border-stone-100 overflow-hidden shadow-sm">
            <table className="w-full table-fixed">
              <OrderTableColumns />
              <thead>
                <tr className="bg-[#1c1c2e]">
                  {[
                    "Invoice",
                    "Date",
                    "Items",
                    "Payment",
                    "Delivery",
                    "Total",
                    "Status",
                  ].map((h, i) => (
                    <>
                      <th
                        key={i}
                        className={`text-[10px] font-bold text-stone-400 uppercase tracking-widest px-5 py-3.5 ${i === 6 ? "text-right" : "text-left"}`}
                      >
                        {h}
                      </th>
                    </>
                  ))}
                </tr>
              </thead>
            </table>
            <div className="h-[560px] w-full">
              <AutoSizer disableHeight>
                {({ width }) => (
                  <VirtualizedList
                    height={560}
                    width={width}
                    rowCount={filtered.length}
                    rowHeight={118}
                    rowRenderer={({ index, key, style }) => {
                      const order = filtered[index];
                      return (
                        <div key={key} style={{ ...style, width: "100%" }}>
                          <table className="w-full table-fixed">
                            <OrderTableColumns />
                            <tbody>
                              <OrderRow
                                order={order}
                                index={index}
                                onView={() => setSelectedOrder(order)}
                              />
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                )}
              </AutoSizer>
            </div>
            <div className="flex items-center justify-between border-t border-stone-100 px-5 py-3 text-xs text-stone-500">
              <span>
                Page {currentPage} of {totalPages} · {pagination.total || 0}{" "}
                orders
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="rounded-lg border border-stone-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className="rounded-lg border border-stone-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <AnimatePresence>
        {selectedOrder && (
          <OrderModal
            order={selectedOrder}
            staffs={staffs}
            onClose={() => setSelectedOrder(null)}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
            currentUserName={user?.name}
            currentUserRole={user?.role}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Orders;
